import React, { useState, useEffect, useCallback } from 'react';
import PracticeES from './PracticeES';
import VocabES from './VocabES';
import LevelTestES from './LevelTestES';
import CommunityES from './CommunityES';
import { createClient } from '@supabase/supabase-js';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import esLocale from '@fullcalendar/core/locales/es';
import emailjs from '@emailjs/browser';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
const WA = 'https://api.whatsapp.com/send?phone=553198011835&text=';

// speaking_club and individual_vip are legacy plans — no longer offered to new
// students, but kept here so existing students on those plans keep their
// current access unchanged. New students get kickstart or elite_squad only.
const PLAN_ACCESS_ES = {
  speaking_club:  ['Club de Conversación', 'Tutoría de Dudas'],
  individual_vip: ['Clase Individual', 'Elite Squad Individual', 'Tutoría de Dudas'],
  elite_squad:    ['Clase Individual', 'Elite Squad Individual', 'Elite Squad', 'Club de Conversación', 'Tutoría de Dudas'],
  kickstart:      ['Elite Squad', 'Club de Conversación'],
};
const PLAN_LABELS = { speaking_club: 'Speaking Club', individual_vip: 'Individual VIP', elite_squad: 'Elite Squad', kickstart: 'Kickstart' };
const PLAN_BADGE  = {
  speaking_club:  { bg: '#dcfce7', color: '#166534' },
  individual_vip: { bg: '#dbeafe', color: '#1d4ed8' },
  elite_squad:    { bg: '#fef3c7', color: '#d97706' },
  kickstart:      { bg: '#dcfce7', color: '#16a349' },
};

const LESSON_CAPACITY = (title) => {
  if (title === 'Clase Individual' || title === 'Elite Squad Individual') return 1;
  if (title === 'Tutoría de Dudas') return 9999;
  return 6;
};

// ── STRINGS ─────────────────────────────────────────────────────────────────
const STRINGS = {
  en: {
    hello: 'Hello',
    logout: 'Log out',
    scheduleBtn: '📅 View calendar and schedule a class',
    myGoal: 'My Goal',
    editGoal: 'Edit ✏️',
    setGoal: 'Set your Spanish goal',
    setGoalSub: 'Tell us why you want to learn Spanish',
    goalExamples: [
      'Travel through Latin America speaking Spanish',
      'Connect with Spanish-speaking clients at work',
      'Move to Spain or Latin America in the next 2 years',
      'Watch series and films without subtitles',
    ],
    goalPlaceholder: 'Or write your own goal here...',
    saveGoal: '🎯 Save Goal',
    updateGoal: '✓ Update Goal',
    savingGoal: 'Saving...',
    setLater: 'Set later',
    checkinTitle: 'Weekly Check-in',
    checkinLater: 'Answer later',
    checkinDone: 'This week\'s check-in ✓',
    badges: '🏅 Your Achievements',
    nextBadge: 'Next:',
    allBadges: '👑 You unlocked all achievements!',
    attendedLessons: 'Lessons Attended',
    attendance: 'Attendance',
    streak: 'Week Streak',
    convClub: 'Conv. Club',
    history: '📋 Attendance History',
    present: 'Present',
    late: 'Late',
    absent: 'Absent',
    reflection: '✍️ Weekly Reflection',
    reflectionSub: 'Your answer goes to your teacher',
    reflectionSent: '✓ Sent',
    reflectionPlaceholder: 'Write your answer in Spanish... don\'t worry about perfection!',
    sendReflection: '📤 Send to Teacher',
    sending: 'Sending...',
    reflectionSaved: '✓ Reflection Sent',
    upcomingClasses: 'classes scheduled',
    upcomingModal: '📅 My Classes',
    noClasses: 'No classes scheduled',
    noClassesSub: 'Book a class in the calendar below!',
    close: 'Close',
    bookSlot: '🎟️ Book a Slot',
    myBooking: '📅 My Booking',
    joinClass: '🎥 JOIN CLASS LINK — CLICK TO ENTER',
    cancelSlot: 'CANCEL MY SLOT',
    confirmBooking: '✅ CONFIRM BOOKING',
    joinWaitlist: '📋 JOIN WAITLIST',
    processing: 'Processing...',
    booked: '✅ SLOT BOOKED!',
    cancelled: '🗑️ SLOT CANCELLED.',
    waitlisted: '📋 ON THE WAITLIST!',
    alreadyBooked: 'You are already enrolled in this class.',
    referralTitle: '🎁 Refer a friend!',
    referralSub: 'Your friend joins, you get a discount month.',
    referralBtn: '💬 Refer via WhatsApp',
    nextClass: '🎯 Your next class',
    legend: {
      individual: 'Individual',
      eliteInd: 'Elite Ind.',
      eliteSquad: 'Elite Squad',
      convClub: 'Conv. Club',
      tutoria: 'Tutoría',
      enrolled: '✅ Enrolled',
      full: '🚫 Full',
      upgrade: '🔒 Upgrade',
    },
    langLabel: 'Language',
    cancelReason: 'Reason (optional)',
    keepSlot: 'Keep slot',
    yesCancel: 'Yes, cancel',
    cancelModal: 'Cancel your slot?',
    cancelSub: 'Your slot will become available to other students.',
    upgradeTitle: 'Want to grow even faster?',
    upgradeSub: 'This class is not available on your current plan',
    scUpsellTitle: 'Conversation Club',
    scUpsellSub: 'Accelerate your fluency with live conversation',
    waUpgrade: '💬 I want to upgrade my plan',
    waNotNow: 'Not now',
    tipsTitle: '💡 Did you know?',
    footerSite: '🌐 Website',
    footerInsta: '📸 Instagram',
    footerWA: '💬 WhatsApp',
  },
  pt: {
    hello: 'Olá',
    logout: 'Sair',
    scheduleBtn: '📅 Ver calendário e agendar uma aula',
    myGoal: 'Meu Objetivo',
    editGoal: 'Editar ✏️',
    setGoal: 'Defina seu objetivo com o espanhol',
    setGoalSub: 'Nos conta por que você quer aprender espanhol',
    goalExamples: [
      'Viajar pela América Latina falando espanhol',
      'Conectar com clientes hispanófonos no trabalho',
      'Morar na Espanha ou América Latina nos próximos 2 anos',
      'Assistir séries e filmes sem legenda',
    ],
    goalPlaceholder: 'Ou escreva seu próprio objetivo aqui...',
    saveGoal: '🎯 Definir Objetivo',
    updateGoal: '✓ Atualizar Objetivo',
    savingGoal: 'Salvando...',
    setLater: 'Definir mais tarde',
    checkinTitle: 'Check-in da Semana',
    checkinLater: 'Responder depois',
    checkinDone: 'Check-in desta semana ✓',
    badges: '🏅 Suas Conquistas',
    nextBadge: 'Próxima:',
    allBadges: '👑 Você desbloqueou todas as conquistas!',
    attendedLessons: 'Aulas Assistidas',
    attendance: 'Presença',
    streak: 'Semanas Seguidas',
    convClub: 'Club Conversação',
    history: '📋 Histórico de Presença',
    present: 'Presente',
    late: 'Atrasado',
    absent: 'Faltou',
    reflection: '✍️ Reflexão da Semana',
    reflectionSub: 'Sua resposta vai para o professor',
    reflectionSent: '✓ Enviada',
    reflectionPlaceholder: 'Escreva sua resposta em espanhol... não se preocupe com a perfeição!',
    sendReflection: '📤 Enviar para o Professor',
    sending: 'Enviando...',
    reflectionSaved: '✓ Reflexão Enviada',
    upcomingClasses: 'aula(s) agendada(s)',
    upcomingModal: '📅 Minhas Aulas',
    noClasses: 'Nenhuma aula agendada',
    noClassesSub: 'Reserve uma aula no calendário abaixo!',
    close: 'Fechar',
    bookSlot: '🎟️ Reservar Vaga',
    myBooking: '📅 Sua Reserva',
    joinClass: '🎥 LINK DA AULA — CLIQUE PARA ENTRAR',
    cancelSlot: 'CANCELAR MINHA VAGA',
    confirmBooking: '✅ CONFIRMAR AGENDAMENTO',
    joinWaitlist: '📋 ENTRAR NA LISTA DE ESPERA',
    processing: 'Processando...',
    booked: '✅ VAGA RESERVADA!',
    cancelled: '🗑️ VAGA CANCELADA.',
    waitlisted: '📋 NA LISTA DE ESPERA!',
    alreadyBooked: 'Você já está inscrito nesta aula.',
    referralTitle: '🎁 Indique um amigo!',
    referralSub: 'Seu amigo entra, você ganha um mês com desconto.',
    referralBtn: '💬 Indicar pelo WhatsApp',
    nextClass: '🎯 Sua próxima aula',
    legend: {
      individual: 'Individual',
      eliteInd: 'Elite Ind.',
      eliteSquad: 'Elite Squad',
      convClub: 'Club Conv.',
      tutoria: 'Tutoría',
      enrolled: '✅ Inscrito',
      full: '🚫 Lotada',
      upgrade: '🔒 Upgrade',
    },
    langLabel: 'Idioma',
    cancelReason: 'Motivo (opcional)',
    keepSlot: 'Manter vaga',
    yesCancel: 'Sim, cancelar',
    cancelModal: 'Cancelar sua vaga?',
    cancelSub: 'Sua vaga voltará a ficar disponível para outros alunos.',
    upgradeTitle: 'Quer evoluir ainda mais rápido?',
    upgradeSub: 'Esta aula não está disponível no seu plano atual',
    scUpsellTitle: 'Club de Conversação',
    scUpsellSub: 'Acelere sua fluência com conversação ao vivo',
    waUpgrade: '💬 Quero fazer upgrade do meu plano',
    waNotNow: 'Agora não',
    tipsTitle: '💡 Você sabia?',
    footerSite: '🌐 Site',
    footerInsta: '📸 Instagram',
    footerWA: '💬 WhatsApp',
  },
  es: {
    hello: 'Hola',
    logout: 'Salir',
    scheduleBtn: '📅 Ver calendario y agendar una clase',
    myGoal: 'Mi Objetivo',
    editGoal: 'Editar ✏️',
    setGoal: 'Define tu objetivo con el español',
    setGoalSub: 'Cuéntanos por qué quieres aprender español',
    goalExamples: [
      'Viajar por América Latina hablando español',
      'Conectar con clientes hispanohablantes en el trabajo',
      'Vivir en España o América Latina en los próximos 2 años',
      'Ver series y películas sin subtítulos',
    ],
    goalPlaceholder: 'O escribe tu propio objetivo aquí...',
    saveGoal: '🎯 Guardar Objetivo',
    updateGoal: '✓ Actualizar Objetivo',
    savingGoal: 'Guardando...',
    setLater: 'Definir más tarde',
    checkinTitle: 'Check-in Semanal',
    checkinLater: 'Responder después',
    checkinDone: 'Check-in de esta semana ✓',
    badges: '🏅 Tus Logros',
    nextBadge: 'Próximo:',
    allBadges: '👑 ¡Desbloqueaste todos los logros!',
    attendedLessons: 'Clases Asistidas',
    attendance: 'Asistencia',
    streak: 'Semanas Seguidas',
    convClub: 'Club Conversación',
    history: '📋 Historial de Asistencia',
    present: 'Presente',
    late: 'Tarde',
    absent: 'Ausente',
    reflection: '✍️ Reflexión Semanal',
    reflectionSub: 'Tu respuesta va al profesor',
    reflectionSent: '✓ Enviada',
    reflectionPlaceholder: '¡Escribe tu respuesta en español... no te preocupes por la perfección!',
    sendReflection: '📤 Enviar al Profesor',
    sending: 'Enviando...',
    reflectionSaved: '✓ Reflexión Enviada',
    upcomingClasses: 'clase(s) agendada(s)',
    upcomingModal: '📅 Mis Clases',
    noClasses: 'No hay clases agendadas',
    noClassesSub: '¡Reserva una clase en el calendario de abajo!',
    close: 'Cerrar',
    bookSlot: '🎟️ Reservar Lugar',
    myBooking: '📅 Tu Reserva',
    joinClass: '🎥 LINK DE CLASE — CLICK PARA ENTRAR',
    cancelSlot: 'CANCELAR MI LUGAR',
    confirmBooking: '✅ CONFIRMAR RESERVA',
    joinWaitlist: '📋 UNIRME A LA LISTA DE ESPERA',
    processing: 'Procesando...',
    booked: '✅ ¡LUGAR RESERVADO!',
    cancelled: '🗑️ LUGAR CANCELADO.',
    waitlisted: '📋 ¡EN LISTA DE ESPERA!',
    alreadyBooked: 'Ya estás inscrito en esta clase.',
    referralTitle: '🎁 ¡Refiere a un amigo!',
    referralSub: 'Tu amigo se une, tú obtienes un mes con descuento.',
    referralBtn: '💬 Referir por WhatsApp',
    nextClass: '🎯 Tu próxima clase',
    legend: {
      individual: 'Individual',
      eliteInd: 'Elite Ind.',
      eliteSquad: 'Elite Squad',
      convClub: 'Club Conv.',
      tutoria: 'Tutoría',
      enrolled: '✅ Inscrito',
      full: '🚫 Lleno',
      upgrade: '🔒 Upgrade',
    },
    langLabel: 'Idioma',
    cancelReason: 'Motivo (opcional)',
    keepSlot: 'Mantener lugar',
    yesCancel: 'Sí, cancelar',
    cancelModal: '¿Cancelar tu lugar?',
    cancelSub: 'Tu lugar volverá a estar disponible para otros estudiantes.',
    upgradeTitle: '¿Quieres crecer aún más rápido?',
    upgradeSub: 'Esta clase no está disponible en tu plan actual',
    scUpsellTitle: 'Club de Conversación',
    scUpsellSub: 'Acelera tu fluidez con conversación en vivo',
    waUpgrade: '💬 Quiero hacer upgrade de mi plan',
    waNotNow: 'Ahora no',
    tipsTitle: '💡 ¿Sabías que?',
    footerSite: '🌐 Sitio web',
    footerInsta: '📸 Instagram',
    footerWA: '💬 WhatsApp',
  },
};

// ── CHECKIN QUESTIONS (all 3 languages) ──────────────────────────────────────
const CHECKIN_QUESTIONS = {
  en: [
    { q: 'How are you feeling about your Spanish this week?', opts: ['🚀 Progressing fast!', '😊 Going well', '😐 Stable', '💪 Need to practice more'] },
    { q: 'Did you practice Spanish outside of classes?', opts: ['✅ Every day', '🔄 A few times', '❌ Couldn\'t manage', '📱 Just watched series'] },
    { q: 'What was your biggest challenge in Spanish this week?', opts: ['🗣️ Speaking fluently', '👂 Understanding natives', '📝 Grammar', '💭 Thinking in Spanish'] },
    { q: 'Are you more confident speaking Spanish than a month ago?', opts: ['🙌 Much more!', '📈 A little more', '➡️ The same', '🤔 Not sure'] },
    { q: 'What motivates you most to learn Spanish?', opts: ['✈️ Travel', '💼 Career', '🌍 Culture', '👥 International connections'] },
  ],
  pt: [
    { q: 'Como você está se sentindo com seu espanhol esta semana?', opts: ['🚀 Evoluindo muito!', '😊 Indo bem', '😐 Estável', '💪 Preciso praticar mais'] },
    { q: 'Você conseguiu praticar espanhol fora das aulas?', opts: ['✅ Todo dia', '🔄 Algumas vezes', '❌ Não consegui', '📱 Só assisti séries'] },
    { q: 'Qual foi o maior desafio no espanhol esta semana?', opts: ['🗣️ Falar com fluidez', '👂 Entender nativos', '📝 Gramática', '💭 Pensar em espanhol'] },
    { q: 'Você está mais confiante para falar espanhol do que há um mês?', opts: ['🙌 Muito mais!', '📈 Um pouco mais', '➡️ Igual', '🤔 Não tenho certeza'] },
    { q: 'O que mais te motiva a aprender espanhol?', opts: ['✈️ Viagens', '💼 Carreira', '🌍 Cultura', '👥 Conexões internacionais'] },
  ],
  es: [
    { q: '¿Cómo te sientes con tu español esta semana?', opts: ['🚀 ¡Progresando mucho!', '😊 Yendo bien', '😐 Estable', '💪 Necesito practicar más'] },
    { q: '¿Practicaste español fuera de clases?', opts: ['✅ Todos los días', '🔄 Algunas veces', '❌ No pude', '📱 Solo vi series'] },
    { q: '¿Cuál fue tu mayor desafío en español esta semana?', opts: ['🗣️ Hablar con fluidez', '👂 Entender a nativos', '📝 Gramática', '💭 Pensar en español'] },
    { q: '¿Estás más seguro hablando español que hace un mes?', opts: ['🙌 ¡Mucho más!', '📈 Un poco más', '➡️ Igual', '🤔 No estoy seguro'] },
    { q: '¿Qué te motiva más a aprender español?', opts: ['✈️ Viajes', '💼 Carrera', '🌍 Cultura', '👥 Conexiones internacionales'] },
  ],
};

// ── REFLECTION PROMPTS ────────────────────────────────────────────────────────
const REFLECTION_PROMPTS = {
  en: {
    Beginner: [
      'Describe your perfect weekend in Spanish. Use simple sentences!',
      'What is your favourite food? Write a few sentences about why you love it.',
      'Describe someone important in your life.',
      'What do you do to relax? Describe it in Spanish.',
      'If you could visit one Spanish-speaking country, where would it be and why?',
    ],
    Intermediate: [
      'Describe a challenge you faced recently and how you overcame it.',
      'If you could have dinner with any Spanish-speaking person in history, who would it be?',
      'What is the most important skill for success in your career?',
      'Talk about a time when you had to make a difficult decision.',
      'How has learning Spanish changed or will change your life?',
    ],
  },
  pt: {
    Beginner: [
      'Descreva seu fim de semana perfeito em espanhol. Use frases simples!',
      'Qual é sua comida favorita? Escreva algumas frases em espanhol.',
      'Descreva alguém importante na sua vida.',
      'O que você faz para relaxar? Descreva em espanhol.',
      'Se você pudesse visitar um país de língua espanhola, qual seria e por quê?',
    ],
    Intermediate: [
      'Descreva um desafio que você enfrentou recentemente e como o superou.',
      'Se você pudesse jantar com qualquer pessoa hispânica da história, quem seria?',
      'Qual é a habilidade mais importante para o sucesso na sua carreira?',
      'Fale sobre uma vez em que você teve que tomar uma decisão difícil.',
      'Como aprender espanhol mudou ou vai mudar a sua vida?',
    ],
  },
  es: {
    Beginner: [
      'Describe tu fin de semana perfecto en español. ¡Usa oraciones simples!',
      '¿Cuál es tu comida favorita? Escribe algunas oraciones sobre por qué te gusta.',
      'Describe a alguien importante en tu vida.',
      '¿Qué haces para relajarte? Descríbelo en español.',
      'Si pudieras visitar un país hispanohablante, ¿cuál sería y por qué?',
    ],
    Intermediate: [
      'Describe un desafío que enfrentaste recientemente y cómo lo superaste.',
      'Si pudieras cenar con cualquier persona hispanohablante de la historia, ¿quién sería?',
      '¿Cuál es la habilidad más importante para el éxito en tu carrera?',
      'Habla de una vez en que tuviste que tomar una decisión difícil.',
      '¿Cómo aprender español ha cambiado o cambiará tu vida?',
    ],
  },
};

const CANCEL_REASONS = {
  en: ['😔 Work commitment', '🤒 Not feeling well', '🚗 Personal issue', '📅 Forgot to cancel earlier', '⚡ Technical problem', '🔄 Will reschedule'],
  pt: ['😔 Compromisso de trabalho', '🤒 Não estou bem', '🚗 Imprevisto pessoal', '📅 Esqueci de cancelar antes', '⚡ Problema técnico', '🔄 Vou reagendar'],
  es: ['😔 Compromiso de trabajo', '🤒 No me siento bien', '🚗 Imprevisto personal', '📅 Olvidé cancelar antes', '⚡ Problema técnico', '🔄 Reagendaré'],
};

const TIPS = {
  en: [
    { icon: '🔥', title: 'Active Output Protocol™', text: '80% of each class time is you speaking. No passivity — the Quevedo Framework puts you in real situations from day one.' },
    { icon: '👥', title: 'Groups at Your Exact Level', text: 'Max 5 people, same level and professional profile. A safe environment to make mistakes, grow and build a global network.' },
    { icon: '🌍', title: 'Real-World Spanish', text: 'Forget outdated textbooks. Our content covers Business, Travel and Global Culture — curated by specialists.' },
    { icon: '📊', title: 'Track Your Progress', text: 'Your attendance rate, achievements and history are all here. Consistency is what separates learners from speakers.' },
    { icon: '🎯', title: 'Set a Goal, Reach It', text: 'Students with a clear objective progress up to 40% faster. Update your goal whenever it evolves.' },
  ],
  pt: [
    { icon: '🔥', title: 'Protocolo Active Output™', text: '80% do tempo de cada aula é você falando. Sem passividade — o Quevedo Framework coloca você em situações reais desde o primeiro dia.' },
    { icon: '👥', title: 'Turmas do Seu Nível Exato', text: 'Máximo de 5 pessoas, mesmo nível e perfil profissional. Ambiente seguro para errar, crescer e criar network global.' },
    { icon: '🌍', title: 'Espanhol do Mundo Real', text: 'Esqueça livros datados. Nosso conteúdo cobre Business, Viagens e Cultura Global — curado por especialistas.' },
    { icon: '📊', title: 'Acompanhe Seu Progresso', text: 'Sua taxa de presença, conquistas e histórico estão todos aqui. Consistência é o que separa quem aprende de quem fala.' },
    { icon: '🎯', title: 'Defina um Objetivo, Alcance-o', text: 'Alunos com um objetivo claro progridem até 40% mais rápido. Atualize seu objetivo sempre que evoluir.' },
  ],
  es: [
    { icon: '🔥', title: 'Protocolo Active Output™', text: 'El 80% del tiempo de cada clase eres tú hablando. Sin pasividad — el Quevedo Framework te pone en situaciones reales desde el primer día.' },
    { icon: '👥', title: 'Grupos de Tu Nivel Exacto', text: 'Máximo 5 personas, mismo nivel y perfil profesional. Un ambiente seguro para equivocarte, crecer y construir una red global.' },
    { icon: '🌍', title: 'Español del Mundo Real', text: 'Olvídate de libros desactualizados. Nuestro contenido cubre Negocios, Viajes y Cultura Global — curado por especialistas.' },
    { icon: '📊', title: 'Sigue Tu Progreso', text: 'Tu tasa de asistencia, logros e historial están todos aquí. La constancia es lo que separa a quien aprende de quien habla.' },
    { icon: '🎯', title: 'Define un Objetivo, Alcánzalo', text: 'Los estudiantes con un objetivo claro progresan hasta un 40% más rápido. Actualiza tu objetivo cuando evolucione.' },
  ],
};

// ── BADGES ───────────────────────────────────────────────────────────────────
// Grouped into categories for display. `category` controls which section a
// badge appears under in the UI; it has no effect on the check() logic.
const BADGE_DEFS = [
  // Attendance & classes
  { id: 'first',     icon: '🎯', color: '#ff6a00', category: 'attendance', check: (d) => d.totalAtt >= 1 },
  { id: 'five',      icon: '⭐', color: '#f59e0b', category: 'attendance', check: (d) => d.totalAtt >= 5 },
  { id: 'ten',       icon: '🔥', color: '#ef4444', category: 'attendance', check: (d) => d.totalAtt >= 10 },
  { id: 'tf',        icon: '💪', color: '#8b5cf6', category: 'attendance', check: (d) => d.totalAtt >= 25 },
  { id: 'fifty',     icon: '🏆', color: '#1a2a6c', category: 'attendance', check: (d) => d.totalAtt >= 50 },
  { id: 'hundred',   icon: '💎', color: '#0d9488', category: 'attendance', check: (d) => d.totalAtt >= 100 },
  { id: 'onefifty',  icon: '🔷', color: '#4338ca', category: 'attendance', check: (d) => d.totalAtt >= 150 },
  { id: 'twofifty',  icon: '👑', color: '#78350f', category: 'attendance', check: (d) => d.totalAtt >= 250 },
  { id: 'launch',    icon: '🚀', color: '#2563eb', category: 'attendance', check: (d) => d.decolagem },
  { id: 'loyal',     icon: '🤝', color: '#16a34a', category: 'attendance', check: (d) => d.maxStreak >= 8 },
  { id: 'unstop',    icon: '⚡', color: '#dc2626', category: 'attendance', check: (d) => d.maxStreak >= 12 },
  { id: 'twentywks', icon: '🌱', color: '#15803d', category: 'attendance', check: (d) => d.maxStreak >= 20 },
  { id: 'yearlong',  icon: '🌳', color: '#166534', category: 'attendance', check: (d) => d.maxStreak >= 52 },
  { id: 'punct',     icon: '🗓️', color: '#0284c7', category: 'attendance', check: (d) => d.totalAtt >= 5 && d.totalCancellations === 0 },
  { id: 'scstar',    icon: '🌟', color: '#7c3aed', category: 'attendance', check: (d) => d.scAtt >= 5 },
  { id: 'confident', icon: '🎤', color: '#be185d', category: 'attendance', check: (d) => d.scAtt >= 10 },
  { id: 'scpro',     icon: '🎙️', color: '#9d174d', category: 'attendance', check: (d) => d.scAtt >= 20 },
  { id: 'sclegend',  icon: '🗣️', color: '#701a3d', category: 'attendance', check: (d) => d.scAtt >= 50 },
  { id: 'levelup',   icon: '📈', color: '#d97706', category: 'attendance', check: (d) => d.hasLevelUp },
  { id: 'elite',     icon: '👑', color: '#1a2a6c', category: 'attendance', check: (d) => d.eliteCompleted },

  // Practice games — volume & consistency
  { id: 'firstgame', icon: '🎮', color: '#7c3aed', category: 'practice', check: (d) => d.totalGames >= 1 },
  { id: 'streak7',   icon: '🔥', color: '#ea580c', category: 'practice', check: (d) => d.gameStreak >= 7 },
  { id: 'streak30',  icon: '💪', color: '#dc2626', category: 'practice', check: (d) => d.gameStreak >= 30 },
  { id: 'streak60',  icon: '⚡', color: '#9333ea', category: 'practice', check: (d) => d.gameStreak >= 60 },
  { id: 'streak90',  icon: '🏆', color: '#d97706', category: 'practice', check: (d) => d.gameStreak >= 90 },
  { id: 'streak180', icon: '🌟', color: '#0369a1', category: 'practice', check: (d) => d.gameStreak >= 180 },
  { id: 'streak365', icon: '👑', color: '#78350f', category: 'practice', check: (d) => d.gameStreak >= 365 },
  { id: 'dedicated', icon: '🧠', color: '#0284c7', category: 'practice', check: (d) => d.totalGames >= 30 },
  { id: 'committed', icon: '🏋️', color: '#be123c', category: 'practice', check: (d) => d.totalGames >= 100 },
  { id: 'devoted',   icon: '🎓', color: '#4c1d95', category: 'practice', check: (d) => d.totalGames >= 250 },
  { id: 'gamemaster',icon: '🏛️', color: '#78350f', category: 'practice', check: (d) => d.totalGames >= 500 },

  // Breadth — how many of the (now 15) different activity types they've tried
  { id: 'explorer',   icon: '🧭', color: '#059669', category: 'breadth', check: (d) => d.gameTypes >= 5 },
  { id: 'versatile',  icon: '🎨', color: '#7c3aed', category: 'breadth', check: (d) => d.gameTypes >= 10 },
  { id: 'allrounder', icon: '🏅', color: '#dc2626', category: 'breadth', check: (d) => d.gameTypes >= 15 },
  { id: 'polyglot',   icon: '🌐', color: '#0284c7', category: 'breadth', check: (d) => d.levelsPlayed >= 6 },

  // Production skills — writing, speaking, dictation, translation
  { id: 'writer',      icon: '📝', color: '#c026d3', category: 'production', check: (d) => d.writingCount >= 1 },
  { id: 'speaker',     icon: '🎙️', color: '#dc2626', category: 'production', check: (d) => d.speakingCount >= 1 },
  { id: 'producer',    icon: '🎬', color: '#a21caf', category: 'production', check: (d) => d.productionCount >= 15 },
  { id: 'prolific',    icon: '🎥', color: '#701a75', category: 'production', check: (d) => d.productionCount >= 50 },
  { id: 'scribe',      icon: '🎧✏️', color: '#0d9488', category: 'production', check: (d) => d.dictationCount >= 1 },
  { id: 'translator',  icon: '🔁', color: '#c2410c', category: 'production', check: (d) => d.translationCount >= 1 },

  // Missions
  { id: 'mission1',  icon: '🌍', color: '#16a34a', category: 'missions', check: (d) => d.totalMissions >= 1 },
  { id: 'mission5',  icon: '🥇', color: '#d97706', category: 'missions', check: (d) => d.totalMissions >= 5 },
  { id: 'mission15', icon: '🧳', color: '#b45309', category: 'missions', check: (d) => d.totalMissions >= 15 },
  { id: 'mission30', icon: '🏔️', color: '#78350f', category: 'missions', check: (d) => d.totalMissions >= 30 },
];

const BADGE_CATEGORY_LABELS = {
  en: { attendance: 'Attendance & Classes', practice: 'Practice Games', breadth: 'Skill Breadth', production: 'Production Skills', missions: 'Real-World Missions' },
  pt: { attendance: 'Presença e Aulas', practice: 'Jogos de Prática', breadth: 'Diversidade de Habilidades', production: 'Habilidades de Produção', missions: 'Missões do Mundo Real' },
  es: { attendance: 'Asistencia y Clases', practice: 'Juegos de Práctica', breadth: 'Diversidad de Habilidades', production: 'Habilidades de Producción', missions: 'Misiones del Mundo Real' },
};

const BADGE_STRINGS = {
  en: {
    first:     { label: 'First Class',    desc: 'Welcome to Quevedo!',                  hint: 'Attend your 1st class' },
    five:      { label: '5 Classes',      desc: 'Consistency is key!',                  hint: 'Attend 5 classes total' },
    ten:       { label: '10 Classes',     desc: 'You\'re getting the rhythm!',           hint: 'Attend 10 classes total' },
    tf:        { label: '25 Classes',     desc: 'Impressive dedication!',               hint: 'Attend 25 classes total' },
    fifty:     { label: '50 Classes',     desc: 'Master in training!',                  hint: 'Attend 50 classes total' },
    hundred:   { label: '100 Classes',    desc: 'Quevedo legend!',                      hint: 'Attend 100 classes total' },
    onefifty:  { label: '150 Classes',    desc: 'Truly exceptional commitment!',        hint: 'Attend 150 classes total' },
    twofifty:  { label: '250 Classes',    desc: 'A Quevedo hall-of-famer!',             hint: 'Attend 250 classes total' },
    launch:    { label: 'Launchpad',      desc: 'First class booked within 24h!',       hint: 'Book 1st class within 24h of signup' },
    loyal:     { label: 'Loyal',          desc: '8 consecutive weeks with class!',      hint: 'Attend for 8 consecutive weeks' },
    unstop:    { label: 'Unstoppable',    desc: '12 consecutive weeks — extraordinary!',hint: 'Attend for 12 consecutive weeks' },
    twentywks: { label: 'Ironclad',       desc: '20 consecutive weeks — remarkable!',   hint: 'Attend for 20 consecutive weeks' },
    yearlong:  { label: 'Full Year',      desc: 'A whole year of weekly classes!',      hint: 'Attend for 52 consecutive weeks' },
    punct:     { label: 'Punctual',       desc: '5+ classes without any cancellation!', hint: 'Attend 5 classes without cancelling' },
    scstar:    { label: 'Conv. Star',     desc: '5 Conversation Club sessions!',        hint: 'Join Conv. Club 5 times' },
    confident: { label: 'Confident',      desc: '10 Conversation Club sessions!',       hint: 'Join Conv. Club 10 times' },
    scpro:     { label: 'Conv. Pro',      desc: '20 Conversation Club sessions!',       hint: 'Join Conv. Club 20 times' },
    sclegend:  { label: 'Conv. Legend',   desc: '50 Conversation Club sessions!',       hint: 'Join Conv. Club 50 times' },
    levelup:   { label: 'Levelling Up',   desc: 'Advanced a level — real growth!',      hint: 'Advance a level with your teacher' },
    elite:     { label: 'Elite',          desc: '12 months of Elite Squad completed!',  hint: 'Complete 12 months of Elite Squad' },
    firstgame: { label: 'First Play',      desc: 'First mini game completed!',            hint: 'Complete your first mini game' },
    streak7:   { label: '7 Game Days',     desc: '7 days of games in a row!',             hint: 'Complete games for 7 days straight' },
    streak30:  { label: '30 Game Days',    desc: '30 days of games in a row!',            hint: 'Complete games for 30 days straight' },
    streak60:  { label: '60 Game Days',    desc: '60 days of games in a row!',            hint: 'Complete games for 60 days straight' },
    streak90:  { label: '90 Game Days',    desc: '90 days of games in a row!',            hint: 'Complete games for 90 days straight' },
    streak180: { label: '180 Game Days',   desc: 'Half a year of daily practice!',        hint: 'Complete games for 180 days straight' },
    streak365: { label: '365 Game Days',   desc: 'A full year, every single day!',        hint: 'Complete games for 365 days straight' },
    dedicated: { label: 'Dedicated',       desc: '30 mini games completed total!',        hint: 'Complete 30 mini games in total' },
    committed: { label: 'Committed',       desc: '100 mini games completed total!',       hint: 'Complete 100 mini games in total' },
    devoted:   { label: 'Devoted',         desc: '250 mini games completed total!',       hint: 'Complete 250 mini games in total' },
    gamemaster:{ label: 'Game Master',     desc: '500 mini games completed total!',       hint: 'Complete 500 mini games in total' },
    explorer:  { label: 'Explorer',        desc: '5 different activity types tried!',     hint: 'Try 5 different types of activity' },
    versatile: { label: 'Versatile',       desc: '10 different activity types tried!',    hint: 'Try 10 different types of activity' },
    allrounder:{ label: 'All-Rounder',     desc: 'Every single activity type tried!',     hint: 'Try all 15 types of activity' },
    polyglot:  { label: 'Polyglot',        desc: 'Practiced at every CEFR level, A1-C2!', hint: 'Practice at all 6 CEFR levels (A1-C2)' },
    writer:    { label: 'Writer',          desc: 'First writing exercise completed!',     hint: 'Complete a Writing exercise' },
    speaker:   { label: 'Speaker',         desc: 'First speaking exercise completed!',    hint: 'Complete a Speaking exercise' },
    producer:  { label: 'Producer',        desc: '15 writing/speaking exercises done!',   hint: 'Complete 15 Writing or Speaking exercises' },
    prolific:  { label: 'Prolific',         desc: '50 writing/speaking exercises done!',   hint: 'Complete 50 Writing or Speaking exercises' },
    scribe:    { label: 'Scribe',          desc: 'First dictation exercise completed!',   hint: 'Complete a Dictation exercise' },
    translator:{ label: 'Translator',      desc: 'First translation exercise completed!', hint: 'Complete a Translation exercise' },
    mission1:  { label: 'Mission Done',    desc: 'First real-world mission completed!',   hint: 'Complete your first real mission' },
    mission5:  { label: 'Field Agent',     desc: '5 real-world missions completed!',      hint: 'Complete 5 missions in the real world' },
    mission15: { label: 'Seasoned Traveler', desc: '15 real-world missions completed!',   hint: 'Complete 15 missions in the real world' },
    mission30: { label: 'World Explorer',   desc: '30 real-world missions completed!',    hint: 'Complete 30 missions in the real world' },
  },
  pt: {
    first:     { label: 'Primeira Aula',  desc: 'Bem-vindo à Quevedo!',                 hint: 'Assista sua 1ª aula' },
    five:      { label: '5 Aulas',        desc: 'Consistência é a chave!',              hint: 'Assista 5 aulas no total' },
    ten:       { label: '10 Aulas',       desc: 'Você está pegando o ritmo!',           hint: 'Assista 10 aulas no total' },
    tf:        { label: '25 Aulas',       desc: 'Dedicação impressionante!',            hint: 'Assista 25 aulas no total' },
    fifty:     { label: '50 Aulas',       desc: 'Mestre em formação!',                  hint: 'Assista 50 aulas no total' },
    hundred:   { label: '100 Aulas',      desc: 'Lenda da Quevedo!',                    hint: 'Assista 100 aulas no total' },
    onefifty:  { label: '150 Aulas',      desc: 'Comprometimento excepcional!',         hint: 'Assista 150 aulas no total' },
    twofifty:  { label: '250 Aulas',      desc: 'Um ícone da Quevedo!',                 hint: 'Assista 250 aulas no total' },
    launch:    { label: 'Decolagem',      desc: 'Primeira aula marcada em 24h!',        hint: 'Agende a 1ª aula em até 24h do cadastro' },
    loyal:     { label: 'Fiel',           desc: '8 semanas consecutivas com aula!',     hint: 'Tenha presença por 8 semanas seguidas' },
    unstop:    { label: 'Imparável',      desc: '12 semanas seguidas — extraordinário!',hint: 'Mantenha presença por 12 semanas seguidas' },
    twentywks: { label: 'Inabalável',     desc: '20 semanas seguidas — notável!',       hint: 'Mantenha presença por 20 semanas seguidas' },
    yearlong:  { label: 'Ano Completo',   desc: 'Um ano inteiro de aulas semanais!',    hint: 'Mantenha presença por 52 semanas seguidas' },
    punct:     { label: 'Pontualidade',   desc: '5+ aulas sem nenhum cancelamento!',    hint: 'Assista 5 aulas sem cancelar nenhuma' },
    scstar:    { label: 'Conv. Star',     desc: '5 aulas do Club de Conversação!',      hint: 'Participe do Club de Conversação 5 vezes' },
    confident: { label: 'Confiante',      desc: '10 aulas do Club de Conversação!',     hint: 'Participe do Club de Conversação 10 vezes' },
    scpro:     { label: 'Conv. Pro',      desc: '20 aulas do Club de Conversação!',     hint: 'Participe do Club de Conversação 20 vezes' },
    sclegend:  { label: 'Lenda da Conv.', desc: '50 aulas do Club de Conversação!',     hint: 'Participe do Club de Conversação 50 vezes' },
    levelup:   { label: 'Evoluindo',      desc: 'Subiu de nível — crescimento real!',   hint: 'Avance de nível com seu professor' },
    elite:     { label: 'Elite',          desc: '12 meses de Elite Squad completados!', hint: 'Complete os 12 meses do Elite Squad' },
    firstgame: { label: 'Primeira Jogada', desc: 'Primeiro mini game concluído!',         hint: 'Complete seu primeiro mini game' },
    streak7:   { label: '7 Dias de Jogos', desc: '7 dias de jogos seguidos!',             hint: 'Complete jogos por 7 dias seguidos' },
    streak30:  { label: '30 Dias de Jogos',desc: '30 dias de jogos seguidos!',            hint: 'Complete jogos por 30 dias seguidos' },
    streak60:  { label: '60 Dias de Jogos',desc: '60 dias de jogos seguidos!',            hint: 'Complete jogos por 60 dias seguidos' },
    streak90:  { label: '90 Dias de Jogos',desc: '90 dias de jogos seguidos!',            hint: 'Complete jogos por 90 dias seguidos' },
    streak180: { label: '180 Dias de Jogos', desc: 'Meio ano de prática diária!',         hint: 'Complete jogos por 180 dias seguidos' },
    streak365: { label: '365 Dias de Jogos', desc: 'Um ano inteiro, todos os dias!',      hint: 'Complete jogos por 365 dias seguidos' },
    dedicated: { label: 'Dedicado',        desc: '30 mini games concluídos no total!',    hint: 'Complete 30 mini games no total' },
    committed: { label: 'Comprometido',    desc: '100 mini games concluídos no total!',   hint: 'Complete 100 mini games no total' },
    devoted:   { label: 'Devoto',          desc: '250 mini games concluídos no total!',   hint: 'Complete 250 mini games no total' },
    gamemaster:{ label: 'Mestre dos Jogos',desc: '500 mini games concluídos no total!',   hint: 'Complete 500 mini games no total' },
    explorer:  { label: 'Explorador',      desc: '5 tipos de atividade diferentes!',      hint: 'Experimente 5 tipos de atividade diferentes' },
    versatile: { label: 'Versátil',        desc: '10 tipos de atividade diferentes!',     hint: 'Experimente 10 tipos de atividade diferentes' },
    allrounder:{ label: 'Completo',        desc: 'Todos os tipos de atividade testados!', hint: 'Experimente os 15 tipos de atividade' },
    polyglot:  { label: 'Poliglota',       desc: 'Praticou em todos os níveis, A1-C2!',   hint: 'Pratique nos 6 níveis do CEFR (A1-C2)' },
    writer:    { label: 'Escritor',        desc: 'Primeiro exercício de escrita feito!',  hint: 'Complete um exercício de Escrita' },
    speaker:   { label: 'Orador',          desc: 'Primeiro exercício de fala feito!',     hint: 'Complete um exercício de Expressão Oral' },
    producer:  { label: 'Produtor',        desc: '15 exercícios de escrita/fala feitos!', hint: 'Complete 15 exercícios de Escrita ou Fala' },
    prolific:  { label: 'Prolífico',        desc: '50 exercícios de escrita/fala feitos!', hint: 'Complete 50 exercícios de Escrita ou Fala' },
    scribe:    { label: 'Escriba',         desc: 'Primeiro ditado concluído!',            hint: 'Complete um exercício de Ditado' },
    translator:{ label: 'Tradutor',        desc: 'Primeira tradução concluída!',          hint: 'Complete um exercício de Tradução' },
    mission1:  { label: 'Missão Cumprida', desc: 'Primeira missão real concluída!',       hint: 'Complete sua primeira missão real' },
    mission5:  { label: 'Agente de Campo', desc: '5 missões reais concluídas!',           hint: 'Complete 5 missões no mundo real' },
    mission15: { label: 'Viajante Experiente', desc: '15 missões reais concluídas!',      hint: 'Complete 15 missões no mundo real' },
    mission30: { label: 'Explorador Global', desc: '30 missões reais concluídas!',        hint: 'Complete 30 missões no mundo real' },
  },
  es: {
    first:     { label: 'Primera Clase',  desc: '¡Bienvenido a Quevedo!',               hint: 'Asiste a tu 1ª clase' },
    five:      { label: '5 Clases',       desc: '¡La constancia es la clave!',           hint: 'Asiste a 5 clases en total' },
    ten:       { label: '10 Clases',      desc: '¡Estás agarrando el ritmo!',           hint: 'Asiste a 10 clases en total' },
    tf:        { label: '25 Clases',      desc: '¡Dedicación impresionante!',            hint: 'Asiste a 25 clases en total' },
    fifty:     { label: '50 Clases',      desc: '¡Maestro en formación!',               hint: 'Asiste a 50 clases en total' },
    hundred:   { label: '100 Clases',     desc: '¡Leyenda de Quevedo!',                 hint: 'Asiste a 100 clases en total' },
    onefifty:  { label: '150 Clases',     desc: '¡Un compromiso excepcional!',          hint: 'Asiste a 150 clases en total' },
    twofifty:  { label: '250 Clases',     desc: '¡Un ícono de Quevedo!',                hint: 'Asiste a 250 clases en total' },
    launch:    { label: 'Despegue',       desc: '¡Primera clase reservada en 24h!',     hint: 'Agenda la 1ª clase en menos de 24h del registro' },
    loyal:     { label: 'Fiel',           desc: '¡8 semanas consecutivas con clase!',   hint: 'Asiste durante 8 semanas seguidas' },
    unstop:    { label: 'Imparable',      desc: '¡12 semanas seguidas — extraordinario!',hint: 'Asiste durante 12 semanas seguidas' },
    twentywks: { label: 'Inquebrantable', desc: '¡20 semanas seguidas — notable!',      hint: 'Asiste durante 20 semanas seguidas' },
    yearlong:  { label: 'Año Completo',   desc: '¡Un año entero de clases semanales!',  hint: 'Asiste durante 52 semanas seguidas' },
    punct:     { label: 'Puntualidad',    desc: '¡5+ clases sin ninguna cancelación!',  hint: 'Asiste a 5 clases sin cancelar ninguna' },
    scstar:    { label: 'Estrella Conv.', desc: '¡5 sesiones del Club de Conversación!',hint: 'Únete al Club de Conversación 5 veces' },
    confident: { label: 'Confiado',       desc: '¡10 sesiones del Club de Conversación!',hint: 'Únete al Club de Conversación 10 veces' },
    scpro:     { label: 'Conv. Pro',      desc: '¡20 sesiones del Club de Conversación!',hint: 'Únete al Club de Conversación 20 veces' },
    sclegend:  { label: 'Leyenda Conv.',  desc: '¡50 sesiones del Club de Conversación!',hint: 'Únete al Club de Conversación 50 veces' },
    levelup:   { label: 'Evolucionando',  desc: '¡Subiste de nivel — crecimiento real!',hint: 'Avanza de nivel con tu profesor' },
    elite:     { label: 'Elite',          desc: '¡12 meses de Elite Squad completados!',hint: 'Completa los 12 meses de Elite Squad' },
    firstgame: { label: 'Primera Jugada',  desc: '¡Primer mini juego completado!',        hint: 'Completa tu primer mini juego' },
    streak7:   { label: '7 Días de Juego', desc: '¡7 días de juegos seguidos!',           hint: 'Completa juegos por 7 días seguidos' },
    streak30:  { label: '30 Días de Juego',desc: '¡30 días de juegos seguidos!',          hint: 'Completa juegos por 30 días seguidos' },
    streak60:  { label: '60 Días de Juego',desc: '¡60 días de juegos seguidos!',          hint: 'Completa juegos por 60 días seguidos' },
    streak90:  { label: '90 Días de Juego',desc: '¡90 días de juegos seguidos!',          hint: 'Completa juegos por 90 días seguidos' },
    streak180: { label: '180 Días de Juego', desc: '¡Medio año de práctica diaria!',       hint: 'Completa juegos por 180 días seguidos' },
    streak365: { label: '365 Días de Juego', desc: '¡Un año entero, todos los días!',      hint: 'Completa juegos por 365 días seguidos' },
    dedicated: { label: 'Dedicado',        desc: '¡30 mini juegos completados en total!', hint: 'Completa 30 mini juegos en total' },
    committed: { label: 'Comprometido',    desc: '¡100 mini juegos completados en total!', hint: 'Completa 100 mini juegos en total' },
    devoted:   { label: 'Devoto',          desc: '¡250 mini juegos completados en total!', hint: 'Completa 250 mini juegos en total' },
    gamemaster:{ label: 'Maestro de Juegos', desc: '¡500 mini juegos completados en total!', hint: 'Completa 500 mini juegos en total' },
    explorer:  { label: 'Explorador',      desc: '¡5 tipos de actividad diferentes!',     hint: 'Prueba 5 tipos de actividad diferentes' },
    versatile: { label: 'Versátil',        desc: '¡10 tipos de actividad diferentes!',    hint: 'Prueba 10 tipos de actividad diferentes' },
    allrounder:{ label: 'Completo',        desc: '¡Todos los tipos de actividad probados!', hint: 'Prueba los 15 tipos de actividad' },
    polyglot:  { label: 'Políglota',       desc: '¡Practicaste en todos los niveles, A1-C2!', hint: 'Practica en los 6 niveles del CEFR (A1-C2)' },
    writer:    { label: 'Escritor',        desc: '¡Primer ejercicio de escritura hecho!', hint: 'Completa un ejercicio de Escritura' },
    speaker:   { label: 'Orador',          desc: '¡Primer ejercicio de expresión oral!',  hint: 'Completa un ejercicio de Expresión Oral' },
    producer:  { label: 'Productor',       desc: '¡15 ejercicios de escritura/oral hechos!', hint: 'Completa 15 ejercicios de Escritura o Expresión Oral' },
    prolific:  { label: 'Prolífico',       desc: '¡50 ejercicios de escritura/oral hechos!', hint: 'Completa 50 ejercicios de Escritura o Expresión Oral' },
    scribe:    { label: 'Escriba',         desc: '¡Primer dictado completado!',           hint: 'Completa un ejercicio de Dictado' },
    translator:{ label: 'Traductor',       desc: '¡Primera traducción completada!',       hint: 'Completa un ejercicio de Traducción' },
    mission1:  { label: 'Misión Cumplida', desc: '¡Primera misión real completada!',      hint: 'Completa tu primera misión real' },
    mission5:  { label: 'Agente de Campo', desc: '¡5 misiones reales completadas!',       hint: 'Completa 5 misiones en el mundo real' },
    mission15: { label: 'Viajero Experto',  desc: '¡15 misiones reales completadas!',     hint: 'Completa 15 misiones en el mundo real' },
    mission30: { label: 'Explorador Global', desc: '¡30 misiones reales completadas!',    hint: 'Completa 30 misiones en el mundo real' },
  },
};

// ── HELPERS ──────────────────────────────────────────────────────────────────
const getWeekNumber = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
};

const calcStreaks = (attRecords) => {
  const present = attRecords.filter(a => a.status === 'present');
  if (!present.length) return { current: 0, max: 0 };
  const getMonday = (d) => {
    const dt = new Date(d); const day = dt.getUTCDay();
    dt.setUTCDate(dt.getUTCDate() - (day === 0 ? 6 : day - 1)); dt.setUTCHours(0,0,0,0);
    return dt.getTime();
  };
  const weeks = [...new Set(present.map(a => getMonday(a.created_at)))].sort((a,b) => b-a);
  const thisWeek = getMonday(new Date()); const lastWeek = thisWeek - 7*24*60*60*1000;
  let current = 0;
  if (weeks[0] === thisWeek || weeks[0] === lastWeek) {
    current = 1;
    for (let i = 1; i < weeks.length; i++) {
      if (weeks[i-1] - weeks[i] === 7*24*60*60*1000) current++; else break;
    }
  }
  let max = 1, temp = 1;
  for (let i = 1; i < weeks.length; i++) {
    if (weeks[i-1] - weeks[i] === 7*24*60*60*1000) { temp++; max = Math.max(max, temp); } else temp = 1;
  }
  return { current, max: Math.max(current, max) };
};

const getPaywallType = (lessonTitle, plan) => {
  if (!lessonTitle || !plan) return null;
  if ((PLAN_ACCESS_ES[plan] || []).includes(lessonTitle)) return null;
  if (plan === 'individual_vip' && lessonTitle === 'Club de Conversación') return 'sc_upsell';
  return 'plan_upgrade';
};

// ── STYLES (same as English portal) ──────────────────────────────────────────
const appStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { background: #f0f4f8; font-family: 'Plus Jakarta Sans', sans-serif; margin: 0; color: #0f172a; }
  .app-wrap { max-width: 960px; margin: 0 auto; padding: 24px 20px 80px; }
  .menu-box { position: relative; border: none; border-radius: 18px; padding: 18px 14px; color: white; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; text-align: left; display: flex; flex-direction: column; gap: 2px; min-height: 118px; transition: transform 0.12s; }
  .menu-box:active { transform: scale(0.97); }
  .menu-box-icon { font-size: 1.8rem; line-height: 1; margin-bottom: 4px; }
  .menu-box-title { font-weight: 900; font-size: 0.92rem; }
  .menu-box-sub { font-weight: 600; font-size: 0.72rem; color: rgba(255,255,255,0.75); }
  .menu-box-arrow { margin-top: auto; padding-top: 8px; font-weight: 800; font-size: 0.78rem; align-self: flex-end; }
  .menu-box-dot { position: absolute; top: 12px; right: 12px; width: 9px; height: 9px; border-radius: 50%; background: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,0.35); }
  @media (max-width: 420px) { .menu-box { padding: 14px 12px; min-height: 106px; } .menu-box-icon { font-size: 1.6rem; } }
  .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.65); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 5000; animation: fadeIn 0.2s ease-out; }
  .modal-box { background: white; padding: 36px 28px; border-radius: 28px; width: 90%; max-width: 420px; text-align: center; box-shadow: 0 30px 60px rgba(0,0,0,0.2); animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1); position: relative; overflow-y: auto; max-height: 92vh; }
  .modal-wide { max-width: 460px; text-align: left; }
  .modal-close { position: absolute; top: 14px; right: 14px; background: #f1f5f9; border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem; color: #64748b; z-index: 10; transition: background 0.15s; }
  .modal-close:hover { background: #e2e8f0; color: #0f172a; }
  .btn-main { width: 100%; padding: 15px; border: none; border-radius: 14px; font-weight: 800; font-size: 0.95rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; margin-top: 10px; transition: opacity 0.15s, transform 0.1s; color: white; }
  .btn-main:active { transform: scale(0.97); }
  .btn-cancel-vaga { background: #fff; color: #ef4444; border: 2px solid #ef4444; }
  .btn-ghost { background: none; border: none; color: #94a3b8; font-weight: 600; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.82rem; padding: 8px; width: 100%; margin-top: 4px; }
  .card-section { background: white; border-radius: 20px; padding: 24px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
  .sec-title { font-size: 0.68rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1.5px; margin: 0 0 14px; }
  .next-banner { background: linear-gradient(135deg, #1a2a6c 0%, #1e40af 100%); border-radius: 20px; padding: 22px 28px; display: flex; align-items: center; justify-content: space-between; gap: 20px; box-shadow: 0 8px 30px rgba(30,58,138,0.28); flex-wrap: wrap; margin-bottom: 20px; }
  .join-btn { display: inline-flex; align-items: center; gap: 8px; background: #ff6a00; color: white; padding: 14px 26px; border-radius: 50px; font-weight: 800; font-size: 0.9rem; text-decoration: none; white-space: nowrap; box-shadow: 0 4px 15px rgba(255,106,0,0.4); transition: transform 0.15s; }
  .join-btn:hover { transform: translateY(-2px); }
  .join-btn-modal { display: flex; align-items: center; justify-content: center; gap: 8px; background: linear-gradient(135deg,#16a34a,#15803d); color: white; padding: 16px; border-radius: 14px; font-weight: 800; font-size: 1rem; text-decoration: none; width: 100%; box-sizing: border-box; margin-top: 12px; transition: opacity 0.15s; }
  .join-btn-modal:hover { opacity: 0.9; }
  .stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
  .stat-pill { background: #f8fafc; border-radius: 14px; padding: 14px 10px; text-align: center; }
  .stat-value { font-size: 1.5rem; font-weight: 900; line-height: 1; }
  .stat-label { font-size: 0.62rem; font-weight: 700; color: #94a3b8; margin-top: 5px; text-transform: uppercase; letter-spacing: 0.5px; line-height: 1.3; }
  .goal-card { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); border: 1.5px solid #bae6fd; border-radius: 16px; padding: 16px 20px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: box-shadow 0.2s; }
  .goal-card:hover { box-shadow: 0 4px 16px rgba(2,132,199,0.15); }
  .goal-empty { background: linear-gradient(135deg, #fafafa, #f1f5f9); border: 2px dashed #cbd5e1; border-radius: 16px; padding: 16px 20px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: all 0.2s; }
  @keyframes badgePop { 0% { transform: scale(0.8); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes badgeShine { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
  .badges-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(84px, 1fr)); gap: 8px; }
  .badge-category-title { font-size: 0.66rem; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin: 18px 0 8px; }
  .badge-category-title:first-of-type { margin-top: 4px; }
  .badge-card { border-radius: 14px; padding: 14px 6px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 5px; position: relative; overflow: hidden; }
  .badge-card.earned { animation: badgePop 0.4s ease forwards; }
  .badge-card.locked { background: #f8fafc; border: 2px dashed #e2e8f0; opacity: 0.72; filter: grayscale(0.5); }
  .badge-icon { font-size: 1.8rem; line-height: 1; position: relative; z-index: 1; }
  .badge-label { font-size: 0.62rem; font-weight: 800; line-height: 1.3; position: relative; z-index: 1; }
  .badge-desc { font-size: 0.55rem; font-weight: 600; line-height: 1.3; position: relative; z-index: 1; display: none; }
  .badge-card.earned .badge-desc { display: block; color: rgba(255,255,255,0.85); }
  .badge-card.locked .badge-desc { display: block; color: #64748b; }
  .badge-card.earned .badge-label { color: white; }
  .badge-card.locked .badge-label { color: #475569; }
  .badge-shine { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 60%); animation: badgeShine 2s ease-in-out infinite; }
  .reflection-card { background: linear-gradient(135deg, #fefce8, #fef9c3); border: 1.5px solid #fde047; border-radius: 20px; padding: 22px; margin-bottom: 20px; }
  .reflection-textarea { width: 100%; padding: 14px; border-radius: 12px; border: 1.5px solid #fde047; background: white; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.9rem; line-height: 1.6; resize: vertical; outline: none; color: #0f172a; box-sizing: border-box; }
  .reflection-textarea:focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.1); }
  .upcoming-item { display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: #f8fafc; border-radius: 12px; margin-bottom: 8px; border: 1.5px solid #f1f5f9; }
  .checkin-opt { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 14px; border: 2px solid #e2e8f0; cursor: pointer; font-weight: 700; font-size: 0.88rem; color: #475569; transition: all 0.15s; text-align: left; background: white; width: 100%; font-family: 'Plus Jakarta Sans', sans-serif; margin-bottom: 8px; }
  .checkin-opt:hover { border-color: #1a2a6c; color: #1a2a6c; background: #f0f4ff; }
  .checkin-opt.selected { border-color: #1a2a6c; background: #1a2a6c; color: white; }
  .att-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px; background: #f8fafc; margin-bottom: 6px; }
  .att-chip { padding: 4px 10px; border-radius: 6px; font-weight: 800; font-size: 0.7rem; white-space: nowrap; }
  .legend-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
  .leg-pill { background: white; border: 1.5px solid #f1f5f9; border-radius: 50px; padding: 7px 14px; font-size: 0.78rem; font-weight: 700; display: flex; align-items: center; gap: 7px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
  .col-dot { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; }
  .referral-wrap { background: linear-gradient(135deg, #ff6a00 0%, #ff8c00 100%); border-radius: 20px; padding: 20px 24px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; box-shadow: 0 6px 20px rgba(255,106,0,0.25); }
  .referral-btn { background: white; color: #ff6a00; padding: 12px 22px; border-radius: 50px; font-weight: 800; font-size: 0.85rem; text-decoration: none; white-space: nowrap; transition: transform 0.15s; display: inline-block; }
  .referral-btn:hover { transform: translateY(-1px); }
  .tips-wrap { background: white; border-radius: 20px; padding: 22px 24px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
  @keyframes tipFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .tip-card { animation: tipFade 0.4s ease; display: flex; gap: 16px; align-items: flex-start; }
  .tip-dots { display: flex; gap: 6px; justify-content: center; margin-top: 18px; }
  .tip-dot { width: 7px; height: 7px; border-radius: 50%; background: #e2e8f0; cursor: pointer; transition: background 0.2s, transform 0.2s; }
  .tip-dot.active { background: #1a2a6c; transform: scale(1.3); }
  .wa-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 14px; font-weight: 800; font-size: 0.9rem; text-decoration: none; width: 100%; box-sizing: border-box; margin-top: 10px; background: #16a34a; color: white; transition: opacity 0.15s; }
  .wa-btn:hover { opacity: 0.9; }
  .lang-toggle { display: flex; gap: 4px; background: #f1f5f9; padding: 4px; border-radius: 10px; }
  .lang-btn { padding: 5px 12px; border-radius: 7px; border: none; font-weight: 700; font-size: 0.75rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; background: transparent; color: #64748b; }
  .lang-btn.active { background: #1a2a6c; color: white; }
  .fc { background: white; padding: 20px; border-radius: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.04); }
  .fc-theme-standard th { border: none !important; padding: 12px 0 !important; font-size: 0.78rem; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .fc-theme-standard td { border-color: #f1f5f9 !important; }
  .fc-scrollgrid { border: none !important; }
  .fc-event { border-radius: 7px !important; border: none !important; box-shadow: 0 2px 6px rgba(0,0,0,0.1); transition: all 0.2s !important; padding: 2px !important; }
  .fc-event:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 5px 14px rgba(0,0,0,0.15); z-index: 10 !important; }
  .fc-event-main { font-weight: 700 !important; font-size: 0.75rem; padding: 3px 5px; }
  .fc-toolbar-title { font-weight: 900 !important; color: #0f172a !important; font-size: 1.3rem !important; }
  .fc-button-primary { background: #1a2a6c !important; border: none !important; border-radius: 10px !important; font-weight: 800 !important; font-size: 0.78rem !important; padding: 9px 16px !important; text-transform: uppercase; }
  .fc-button-active { background: #ff6a00 !important; }
  .fc-day-today { background-color: #f0f9ff !important; }
  @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
  @keyframes slideUp { from { transform: translateY(60px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
  .app-footer { border-top: 1px solid #e2e8f0; margin-top: 32px; padding-top: 24px; padding-bottom: 8px; text-align: center; }
  .app-footer-links { display: flex; justify-content: center; gap: 24px; margin-bottom: 12px; flex-wrap: wrap; }
  .app-footer-link { font-size: 0.82rem; font-weight: 700; color: #64748b; text-decoration: none; transition: color 0.2s; }
  .app-footer-link:hover { color: #1a2a6c; }
  .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: white; border-top: 1px solid #e2e8f0; display: flex; z-index: 1000; padding: 8px 0 env(safe-area-inset-bottom, 8px); box-shadow: 0 -4px 16px rgba(0,0,0,0.06); }
  .bottom-nav-btn { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 8px 4px; background: none; border: none; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; }
  .bottom-nav-btn .nav-icon { font-size: 1.3rem; line-height: 1; }
  .bottom-nav-btn .nav-label { font-size: 0.62rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
  @media (max-width: 768px) {
    .app-wrap { padding: 12px 12px 60px; }
    .fc { padding: 12px 8px; border-radius: 16px; }
    .fc-toolbar { flex-direction: column; gap: 12px; }
    .fc-toolbar-chunk { display: flex; justify-content: center; }
    .fc-button-primary { padding: 8px 12px !important; font-size: 0.72rem !important; }
    .modal-box { position: absolute; bottom: 0; border-radius: 28px 28px 0 0; padding-bottom: 48px; max-width: 100%; width: 100%; }
    .stats-row { grid-template-columns: repeat(2, 1fr); }
    .badges-grid { grid-template-columns: repeat(3, 1fr); }
    .referral-wrap { flex-direction: column; }
    .referral-btn { width: 100%; text-align: center; }
    .next-banner { flex-direction: column; align-items: flex-start; }
    .join-btn { width: 100%; justify-content: center; }
  }
`;

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function AppES({ session, setSession }) {
  const [lang, setLang] = useState(() => localStorage.getItem(`quevedo_lang_${session?.id}`) || session?.ui_language || 'en');
  const [activeTab, setActiveTab] = useState('portal');
  const [gameCompletions, setGameCompletions] = useState([]);
  const S = STRINGS[lang];

  const [events, setEvents] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [status, setStatus] = useState('');
  const [nextClass, setNextClass] = useState(null);
  const [myAttendance, setMyAttendance] = useState([]);
  const [lessonMaterials, setLessonMaterials] = useState([]);
  const [myCancellations, setMyCancellations] = useState([]);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [hasLevelUp, setHasLevelUp] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [gameData, setGameData] = useState({ totalGames: 0, gameStreak: 0, gameTypes: 0, levelsPlayed: 0, writingCount: 0, speakingCount: 0, productionCount: 0, dictationCount: 0, translationCount: 0, totalMissions: 0 });
  const [, setPaywallType] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);

  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [goalSaving, setGoalSaving] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);

  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkinSelected, setCheckinSelected] = useState('');
  const [thisWeekCheckin, setThisWeekCheckin] = useState(null);
  const [checkinSaving, setCheckinSaving] = useState(false);

  const [reflectionText, setReflectionText] = useState('');
  const [thisWeekReflection, setThisWeekReflection] = useState(null);
  const [reflectionSaving, setReflectionSaving] = useState(false);
  const [reflectionSaved, setReflectionSaved] = useState(false);

  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const switchLang = (l) => {
    setLang(l);
    localStorage.setItem(`quevedo_lang_${session?.id}`, l);
  };

  useEffect(() => {
    const t = setInterval(() => setTipIdx(i => (i + 1) % TIPS[lang].length), 5000);
    return () => clearInterval(t);
  }, [lang]);

  const loadAllData = useCallback(async (studentData) => {
    const weekNum = getWeekNumber();
    const yearNum = new Date().getFullYear();
    const clean = studentData.email.toLowerCase().trim();
    const userPlan = studentData.plan || 'elite_squad';
    const userLevel = studentData.level || 'Beginner';
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const [
      { data: lessons },
      { data: signups },
      { data: att },
      { data: canc },
      { data: checkins },
      { data: reflections },
      { data: materials },
      { data: levelProg },
      { data: gameComps },
      { data: missionComps },
    ] = await Promise.all([
      supabase.from('lessons_es').select('*').gte('class_time', today.toISOString()),
      supabase.from('signups_es').select('lesson_id, student_email'),
      supabase.from('attendance_es').select('*').eq('student_email', clean),
      supabase.from('cancellations_es').select('*').eq('student_email', clean),
      supabase.from('checkins').select('*').eq('student_id', studentData.id).eq('week_number', weekNum).eq('year', yearNum),
      supabase.from('reflections').select('*').eq('student_id', studentData.id).eq('week_number', weekNum).eq('year', yearNum),
      supabase.from('lesson_materials_es').select('*'),
      supabase.from('level_progression').select('id').eq('student_id', studentData.id).limit(1),
      supabase.from('game_completions').select('played_date, game_type').eq('student_id', studentData.id),
      supabase.from('game_completions').select('played_date, game_type').eq('student_id', studentData.id),
      supabase.from('mission_completions').select('id').eq('student_id', studentData.id),
    ]);

    const attData = att || [];
    const cancData = canc || [];
    const checkinData = checkins && checkins.length > 0 ? checkins[0] : null;
    const reflectionData = reflections && reflections.length > 0 ? reflections[0] : null;

    setMyAttendance(attData);
    setLessonMaterials(materials || []);
    setMyCancellations(cancData);

    // Game badge data — computed here and stored in state
    const todayISO = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const gameDates = [...new Set((gameComps || []).map(c => c.played_date))].sort().reverse();
    let gStreak = 0;
    if (gameDates.length && (gameDates[0] === todayISO || gameDates[0] === yesterday)) {
      gStreak = 1;
      for (let i = 1; i < gameDates.length; i++) {
        if ((new Date(gameDates[i-1]) - new Date(gameDates[i])) / 86400000 === 1) gStreak++;
        else break;
      }
    }
    setGameData({
      totalGames: (gameComps || []).length,
      gameStreak: gStreak,
      // BUG FIX: game_type is stored as "type:level" (e.g. "flashcard:A1"), so counting
      // unique raw strings over-counted every level a type was played at as a separate
      // "type". Split off the level so this reflects distinct activity types played.
      gameTypes: new Set((gameComps || []).map(c => (c.game_type || '').split(':')[0])).size,
      levelsPlayed: new Set((gameComps || []).map(c => (c.game_type || '').split(':')[1]).filter(Boolean)).size,
      writingCount: (gameComps || []).filter(c => (c.game_type || '').split(':')[0] === 'writing').length,
      speakingCount: (gameComps || []).filter(c => (c.game_type || '').split(':')[0] === 'speaking').length,
      productionCount: (gameComps || []).filter(c => { const t = (c.game_type || '').split(':')[0]; return t === 'writing' || t === 'speaking'; }).length,
      dictationCount: (gameComps || []).filter(c => (c.game_type || '').split(':')[0] === 'dictation').length,
      translationCount: (gameComps || []).filter(c => (c.game_type || '').split(':')[0] === 'translation').length,
      totalMissions: (missionComps || []).length,
    });
    setThisWeekCheckin(checkinData);
    setThisWeekReflection(reflectionData);
    setHasLevelUp(!!(levelProg && levelProg.length > 0));
    setGameCompletions(gameComps || []);

    if (reflectionData) { setReflectionText(reflectionData.response || ''); setReflectionSaved(true); }
    if (!checkinData) setTimeout(() => setCheckinOpen(true), 800);
    if (!studentData.personal_goal) setTimeout(() => setGoalModalOpen(true), checkinData ? 500 : 2800);
    if (!lessons) return;

    const filtered = lessons.filter(l => {
      const paywall = getPaywallType(l.title, userPlan);
      if (paywall) return true;
      const levelMap = { Beginner: ['Beginner', 'Speaking'], Intermediate: ['Intermediate', 'Speaking'] };
      const allowed = levelMap[userLevel] || ['Speaking'];
      return allowed.includes(l.level) || l.title === 'Tutoría de Dudas';
    });

    const enrolledFuture = filtered
      .filter(l => (signups || []).some(s => s.lesson_id === l.id && s.student_email === clean))
      .sort((a, b) => new Date(a.class_time) - new Date(b.class_time));
    setUpcomingLessons(enrolledFuture);
    setNextClass(enrolledFuture.find(l => l.meet_link) || null);

    const formattedEvents = filtered.map(l => {
      const paywall = getPaywallType(l.title, userPlan);
      const lSignups = (signups || []).filter(s => s.lesson_id === l.id);
      const cap = LESSON_CAPACITY(l.title);
      const isFull = lSignups.length >= cap;
      const isEnrolled = lSignups.some(s => s.student_email === clean);
      const isLocked = !!paywall;

      let color = '#1e3a8a';
      if (l.title === 'Elite Squad') color = '#7e22ce';
      else if (l.title === 'Club de Conversación') color = '#0d9488';
      else if (l.title === 'Tutoría de Dudas') color = '#0284c7';
      else if (l.title === 'Elite Squad Individual') color = '#d97706';
      if (isEnrolled) color = '#16a34a';
      else if (isFull && !isLocked) color = '#dc2626';
      else if (isLocked) color = '#94a3b8';

      const prefix = isEnrolled ? '✅ ' : isFull && !isLocked ? '🚫 ' : isLocked ? '🔒 ' : '';

      return {
        id: l.id,
        title: `${prefix}${l.title}${l.teacher_name ? ` (${l.teacher_name})` : ''}`,
        start: l.class_time,
        end: new Date(new Date(l.class_time).getTime() + 3600000).toISOString(),
        backgroundColor: color, borderColor: color,
        extendedProps: { isFull, isEnrolled, isLocked, paywall, time: l.class_time, meet_link: l.meet_link || null, rawTitle: l.title },
      };
    });
    setEvents(formattedEvents);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session) loadAllData(session);
  }, [session, loadAllData]);

  const handleAction = async () => {
    setStatus('loading');
    const clean = session.email.toLowerCase().trim();
    if (selectedLesson.extendedProps.isFull) {
      const { error } = await supabase.from('waitlist_es').insert([{ lesson_id: selectedLesson.id, student_email: clean }]);
      if (!error) { setStatus('waitlist'); setTimeout(() => setSelectedLesson(null), 2000); }
      else setStatus('error');
    } else {
      const { data: existing } = await supabase.from('signups_es').select('id').eq('lesson_id', selectedLesson.id).eq('student_email', clean).maybeSingle();
      if (existing) { setStatus('already'); return; }
      const { error } = await supabase.from('signups_es').insert([{ lesson_id: selectedLesson.id, student_email: clean }]);
      if (!error) {
        emailjs.send(process.env.REACT_APP_EMAILJS_SERVICE_ID, process.env.REACT_APP_EMAILJS_TEMPLATE_ID, {
          to_email: clean, lesson_title: selectedLesson.extendedProps.rawTitle,
          lesson_time: new Date(selectedLesson.extendedProps.time).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }),
          meet_link: selectedLesson.extendedProps.meet_link || '',
        }, process.env.REACT_APP_EMAILJS_PUBLIC_KEY).catch(() => {});
        setStatus('success'); setTimeout(() => window.location.reload(), 1500);
      } else setStatus('error');
    }
  };

  const confirmCancel = async () => {
    setStatus('loading');
    const clean = session.email.toLowerCase().trim();
    await supabase.from('cancellations_es').insert({ student_email: clean, lesson_id: selectedLesson.id, reason: cancelReason || null });
    const { error } = await supabase.from('signups_es').delete().match({ student_email: clean, lesson_id: selectedLesson.id });
    if (!error) { setCancelModalOpen(false); setStatus('cancelled'); setTimeout(() => window.location.reload(), 1500); }
    else setStatus('error');
  };

  const saveGoal = async () => {
    if (!goalText.trim()) return;
    setGoalSaving(true);
    await supabase.from('students').update({ personal_goal: goalText.trim(), goal_set_at: new Date().toISOString() }).eq('id', session.id);
    const updated = { ...session, personal_goal: goalText.trim() };
    setSession(updated);
    localStorage.setItem('quevedo_session', JSON.stringify(updated));
    setGoalSaving(false); setGoalModalOpen(false); setEditingGoal(false);
  };

  const saveCheckin = async (answer) => {
    setCheckinSelected(answer); setCheckinSaving(true);
    const weekNum = getWeekNumber(); const yearNum = new Date().getFullYear();
    const q = CHECKIN_QUESTIONS[lang][weekNum % CHECKIN_QUESTIONS[lang].length];
    await supabase.from('checkins').upsert({ student_id: session.id, question: q.q, answer, week_number: weekNum, year: yearNum }, { onConflict: 'student_id,week_number,year' });
    setThisWeekCheckin({ question: q.q, answer });
    setCheckinSaving(false); setTimeout(() => setCheckinOpen(false), 1000);
  };

  const saveReflection = async () => {
    if (!reflectionText.trim()) return;
    setReflectionSaving(true);
    const weekNum = getWeekNumber(); const yearNum = new Date().getFullYear();
    const prompts = REFLECTION_PROMPTS[lang][session?.level] || REFLECTION_PROMPTS[lang]['Beginner'];
    const prompt = prompts[weekNum % prompts.length];
    await supabase.from('reflections').upsert({ student_id: session.id, prompt, response: reflectionText.trim(), week_number: weekNum, year: yearNum }, { onConflict: 'student_id,week_number,year' });
    setThisWeekReflection({ prompt, response: reflectionText.trim() });
    setReflectionSaving(false); setReflectionSaved(true);
  };

  const userPlan = session?.plan || 'elite_squad';
  const totalAtt = myAttendance.filter(a => a.status === 'present').length;
  const totalMarked = myAttendance.length;
  const attRate = totalMarked > 0 ? Math.round((totalAtt / totalMarked) * 100) : null;
  const { current: streak, max: maxStreak } = calcStreaks(myAttendance);
  const scAtt = myAttendance.filter(a => a.status === 'present' && a.rawTitle === 'Club de Conversación').length;
  const totalCancellations = myCancellations.length;
  const eliteCompleted = session?.elite_completed || false;
  const firstAtt = [...myAttendance].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
  const decolagem = !!(firstAtt?.created_at && session?.created_at && (new Date(firstAtt.created_at) - new Date(session.created_at)) <= 48 * 60 * 60 * 1000);
  const badgeData = { totalAtt, maxStreak, scAtt, totalCancellations, hasLevelUp, eliteCompleted, decolagem, ...gameData };

  const weekNum = getWeekNumber();
  const reflectionPrompts = REFLECTION_PROMPTS[lang][session?.level] || REFLECTION_PROMPTS[lang]['Beginner'];
  const thisWeekPrompt = reflectionPrompts[weekNum % reflectionPrompts.length];
  const thisWeekCheckinQ = CHECKIN_QUESTIONS[lang][weekNum % CHECKIN_QUESTIONS[lang].length];
  const isMobile = window.innerWidth <= 768;
  const tips = TIPS[lang];
  const tip = tips[tipIdx % tips.length];
  const levelColor = { Beginner: '#1d4ed8', Intermediate: '#7c3aed' };
  const referralMsg = encodeURIComponent(`Oi! Quero indicar um amigo para o Idiomas Quevedo (Espanhol). Meu nome é ${session?.full_name || ''}. Como funciona o desconto de indicação?`);
  const calLocale = lang === 'es' ? esLocale : ptBrLocale;

  return (
    <>
      <style>{appStyles}</style>
      <div className="app-wrap">

        {/* Bottom Navigation */}
        <nav className="bottom-nav">
          {[['portal','📅','Portal'],['practice','🎮', lang === 'es' ? 'Practicar' : lang === 'en' ? 'Practice' : 'Praticar'],['vocab','📖', lang === 'es' ? 'Vocab' : lang === 'en' ? 'Vocab' : 'Vocab']].map(([key, icon, label]) => (
            <button key={key} className="bottom-nav-btn" onClick={() => setActiveTab(key)}>
              <span className="nav-icon">{icon}</span>
              <span className="nav-label" style={{ color: activeTab === key ? '#dc2626' : '#94a3b8' }}>{label}</span>
            </button>
          ))}
        </nav>

        {/* Practice Tab */}
        {activeTab === 'practice' && (
          <PracticeES session={session} lang={lang} onBack={() => setActiveTab('portal')} />
        )}

        {activeTab === 'vocab' && (
          <VocabES session={session} lang={lang} onBack={() => setActiveTab('portal')} />
        )}

        {activeTab === 'test' && (
          <LevelTestES session={session} lang={lang} onBack={() => setActiveTab('portal')} />
        )}

        {activeTab === 'community' && (
          <CommunityES session={session} lang={lang} onBack={() => setActiveTab('portal')} />
        )}

        {/* Portal Tab */}
        {activeTab === 'portal' && <>

        {/* Header */}
        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', paddingTop: '8px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: '900', marginBottom: '14px', boxShadow: '0 4px 14px rgba(220,38,38,0.25)' }}>
            {session.full_name.charAt(0).toUpperCase()}
          </div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: '900', margin: '0 0 10px' }}>{S.hello}, {session.full_name.split(' ')[0]}! 👋</h1>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '12px' }}>
            <span style={{ padding: '6px 16px', borderRadius: '50px', background: levelColor[session.level] || '#64748b', color: 'white', fontWeight: '800', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{session.level}</span>
            <span style={{ padding: '6px 14px', borderRadius: '50px', background: (PLAN_BADGE[userPlan] || {}).bg, color: (PLAN_BADGE[userPlan] || {}).color, fontWeight: '800', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{PLAN_LABELS[userPlan]}</span>
            <span style={{ padding: '6px 14px', borderRadius: '50px', background: '#fee2e2', color: '#dc2626', fontWeight: '800', fontSize: '0.72rem', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <img src="https://flagcdn.com/es.svg" alt="" style={{ width: '16px', height: '12px', borderRadius: '2px', objectFit: 'cover' }} /> Español
            </span>
            <button onClick={() => setUpcomingOpen(true)} style={{ padding: '6px 14px', borderRadius: '50px', background: '#f1f5f9', color: '#1a2a6c', fontWeight: '800', fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>
              📅 {upcomingLessons.length} {S.upcomingClasses}
            </button>
            <button onClick={() => { localStorage.clear(); window.location.reload(); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem' }}>{S.logout}</button>
          </div>
          {/* Language toggle */}
          <div className="lang-toggle">
            {['en', 'pt', 'es'].map(l => {
              const flagInfo = { en: { code: 'gb', label: 'EN' }, pt: { code: 'br', label: 'PT' }, es: { code: 'es', label: 'ES' } }[l];
              return (
                <button key={l} className={`lang-btn ${lang === l ? 'active' : ''}`} onClick={() => switchLang(l)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <img src={`https://flagcdn.com/${flagInfo.code}.svg`} alt="" style={{ width: '18px', height: '13px', borderRadius: '2px', objectFit: 'cover', flexShrink: 0 }} />
                  {flagInfo.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* Schedule CTA */}
        <a href="#cal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', padding: '16px 24px', borderRadius: '16px', fontWeight: '800', fontSize: '1rem', textDecoration: 'none', marginBottom: '16px', boxShadow: '0 4px 16px rgba(220,38,38,0.25)' }}>
          {S.scheduleBtn}
        </a>


        {/* ── 4-BOX MENU GRID ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

          {/* PRACTICE */}
          <button onClick={() => setActiveTab('practice')} className="menu-box" style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}>
            {!gameCompletions.some(c => c.played_date === new Date().toISOString().split('T')[0]) && (
              <span className="menu-box-dot" />
            )}
            <span className="menu-box-icon">🎮</span>
            <div className="menu-box-title">
              {lang === 'es' ? 'Práctica' : lang === 'en' ? 'Practice' : 'Praticar'}
            </div>
            <div className="menu-box-sub">
              {lang === 'es' ? 'Elige tu nivel y actividad' : lang === 'en' ? 'Choose your level and activity' : 'Escolha seu nível e atividade'}
            </div>
            <span className="menu-box-arrow">{lang === 'es' ? 'Practicar →' : lang === 'en' ? 'Practice →' : 'Praticar →'}</span>
          </button>

          {/* VOCAB NOTEBOOK */}
          <button onClick={() => setActiveTab('vocab')} className="menu-box" style={{ background: 'linear-gradient(135deg,#0284c7,#0369a1)', boxShadow: '0 4px 16px rgba(2,132,199,0.3)' }}>
            <span className="menu-box-icon">📖</span>
            <div className="menu-box-title">
              {lang === 'es' ? 'Vocabulario' : lang === 'en' ? 'Vocabulary' : 'Vocabulário'}
            </div>
            <div className="menu-box-sub">
              {lang === 'es' ? 'Guarda y repasa' : lang === 'en' ? 'Save & review' : 'Salve e revise'}
            </div>
            <span className="menu-box-arrow">{lang === 'es' ? 'Abrir →' : lang === 'en' ? 'Open →' : 'Abrir →'}</span>
          </button>

          {/* LEVEL TEST */}
          <button onClick={() => setActiveTab('test')} className="menu-box" style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow: '0 4px 16px rgba(220,38,38,0.25)' }}>
            <span className="menu-box-icon">🎯</span>
            <div className="menu-box-title">
              {lang === 'es' ? 'Test de nivel' : lang === 'en' ? 'Level test' : 'Teste de nível'}
            </div>
            <div className="menu-box-sub">
              {lang === 'es' ? '20 preguntas' : lang === 'en' ? '20 questions' : '20 perguntas'}
            </div>
            <span className="menu-box-arrow">{lang === 'es' ? 'Iniciar →' : lang === 'en' ? 'Start →' : 'Iniciar →'}</span>
          </button>

          {/* COMMUNITY */}
          <button onClick={() => setActiveTab('community')} className="menu-box" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', boxShadow: '0 4px 16px rgba(22,163,74,0.3)' }}>
            <span className="menu-box-icon">🌎</span>
            <div className="menu-box-title">
              {lang === 'es' ? 'Comunidad' : lang === 'en' ? 'Community' : 'Comunidade'}
            </div>
            <div className="menu-box-sub">
              {lang === 'es' ? 'Chat y recomendaciones' : lang === 'en' ? 'Chat & recommendations' : 'Chat e recomendações'}
            </div>
            <span className="menu-box-arrow">{lang === 'es' ? 'Entrar →' : lang === 'en' ? 'Enter →' : 'Entrar →'}</span>
          </button>

        </div>

        {/* Personal goal */}
        {session.personal_goal ? (
          <div className="goal-card" onClick={() => { setGoalText(session.personal_goal); setEditingGoal(true); setGoalModalOpen(true); }}>
            <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🎯</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '4px' }}>{S.myGoal}</div>
              <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.9rem', lineHeight: 1.4 }}>{session.personal_goal}</div>
            </div>
            <span style={{ fontSize: '0.72rem', color: '#0284c7', fontWeight: '700', flexShrink: 0 }}>{S.editGoal}</span>
          </div>
        ) : (
          <div className="goal-empty" onClick={() => setGoalModalOpen(true)}>
            <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🎯</span>
            <div>
              <div style={{ fontWeight: '800', color: '#64748b', fontSize: '0.9rem' }}>{S.setGoal}</div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600', marginTop: '3px' }}>{S.setGoalSub}</div>
            </div>
          </div>
        )}

        {/* Check-in result */}
        {thisWeekCheckin && (
          <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1.5px solid #86efac', borderRadius: '14px', padding: '12px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.3rem' }}>✅</span>
            <div>
              <div style={{ fontWeight: '800', color: '#166534', fontSize: '0.82rem' }}>{S.checkinDone}</div>
              <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '2px', fontWeight: '600' }}>"{thisWeekCheckin.answer}"</div>
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="card-section">
          {totalMarked > 0 && (
            <div className="stats-row">
              <div className="stat-pill"><div className="stat-value" style={{ color: '#1a2a6c' }}>{totalAtt}</div><div className="stat-label">{S.attendedLessons}</div></div>
              <div className="stat-pill"><div className="stat-value" style={{ color: attRate >= 80 ? '#16a34a' : attRate >= 60 ? '#d97706' : '#dc2626' }}>{attRate}%</div><div className="stat-label">{S.attendance}</div></div>
              <div className="stat-pill"><div className="stat-value" style={{ color: streak >= 3 ? '#ef4444' : '#64748b' }}>{streak}🔥</div><div className="stat-label">{S.streak}</div></div>
              <div className="stat-pill"><div className="stat-value" style={{ color: '#0d9488' }}>{scAtt}</div><div className="stat-label">{S.convClub}</div></div>
            </div>
          )}
          <p className="sec-title">{S.badges}</p>
          {['attendance', 'practice', 'breadth', 'production', 'missions'].map(cat => {
            const catBadges = BADGE_DEFS.filter(b => b.category === cat);
            if (!catBadges.length) return null;
            return (
              <div key={cat}>
                <p className="badge-category-title">{BADGE_CATEGORY_LABELS[lang][cat]}</p>
                <div className="badges-grid">
                  {catBadges.map(b => {
                    const earned = b.check(badgeData);
                    const bs = BADGE_STRINGS[lang][b.id];
                    return (
                      <div key={b.id} className={`badge-card ${earned ? 'earned' : 'locked'}`}
                        style={earned ? { background: `linear-gradient(135deg, ${b.color}dd, ${b.color})` } : {}}>
                        {earned && <div className="badge-shine" />}
                        <span className="badge-icon">{b.icon}</span>
                        <span className="badge-label">{bs.label}</span>
                        <span className="badge-desc">{earned ? bs.desc : bs.hint}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {(() => {
            const nextBadge = BADGE_DEFS.find(b => !b.check(badgeData));
            if (!nextBadge) return (
              <div style={{ marginTop: '14px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', borderRadius: '12px', padding: '14px 16px', textAlign: 'center' }}>
                <span style={{ color: 'white', fontWeight: '800', fontSize: '0.9rem' }}>{S.allBadges}</span>
              </div>
            );
            const bs = BADGE_STRINGS[lang][nextBadge.id];
            return (
              <div style={{ marginTop: '14px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', border: '1.5px solid #86efac' }}>
                <span style={{ fontSize: '1.4rem' }}>{nextBadge.icon}</span>
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#166534' }}>{S.nextBadge} <strong>{bs.label}</strong></div>
                  <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: '600', marginTop: '2px' }}>{bs.hint}</div>
                </div>
              </div>
            );
          })()}
          {myAttendance.length > 0 && (
            <>
              <p className="sec-title" style={{ marginTop: '22px' }}>{S.history}</p>
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {[...myAttendance].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10).map(rec => {
                  const mat = lessonMaterials.find(m => m.lesson_id === rec.lesson_id);
                  const statusLabel = rec.status === 'present' ? S.present : rec.status === 'late' ? S.late : S.absent;
                  return (
                    <div key={rec.id} className="att-row">
                      <span className="att-chip" style={{ background: rec.status === 'present' ? '#dcfce7' : rec.status === 'late' ? '#fef3c7' : '#fee2e2', color: rec.status === 'present' ? '#166534' : rec.status === 'late' ? '#92400e' : '#991b1b' }}>
                        {statusLabel}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.82rem' }}>{rec.lesson_id}</div>
                        {rec.note && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>{rec.note}</div>}
                        {mat && (
                          <div style={{ marginTop: '4px', fontSize: '0.72rem' }}>
                            {mat.content_covered && <div><span style={{ fontWeight: '700', color: '#0284c7' }}>📚</span> {mat.content_covered}</div>}
                            {mat.homework && <div style={{ marginTop: '2px' }}><span style={{ fontWeight: '700', color: '#d97706' }}>📝</span> {mat.homework}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Reflection */}
        <div className="reflection-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <span style={{ fontSize: '1.3rem' }}>✍️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '800', color: '#713f12', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{S.reflection}</div>
              <div style={{ fontSize: '0.68rem', color: '#92400e', fontWeight: '600', marginTop: '2px' }}>{S.reflectionSub}</div>
            </div>
            {(thisWeekReflection?.response || reflectionSaved) && (
              <span style={{ background: '#fde047', borderRadius: '50px', padding: '3px 10px', fontSize: '0.65rem', fontWeight: '800', color: '#713f12', flexShrink: 0 }}>{S.reflectionSent}</span>
            )}
          </div>
          <div style={{ fontWeight: '700', color: '#713f12', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '14px', fontStyle: 'italic' }}>💬 {thisWeekPrompt}</div>
          <textarea className="reflection-textarea" rows={4} placeholder={S.reflectionPlaceholder} value={reflectionText} onChange={e => { setReflectionText(e.target.value); setReflectionSaved(false); }} />
          <button onClick={saveReflection} disabled={reflectionSaving || !reflectionText.trim()}
            style={{ marginTop: '12px', padding: '11px 22px', background: reflectionText.trim() ? 'linear-gradient(135deg, #d97706, #b45309)' : '#f1f5f9', color: reflectionText.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: reflectionText.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans', fontSize: '0.85rem' }}>
            {reflectionSaving ? S.sending : reflectionSaved ? S.reflectionSaved : S.sendReflection}
          </button>
        </div>

        {/* Tips */}
        <div className="tips-wrap">
          <p className="sec-title">{S.tipsTitle}</p>
          <div className="tip-card" key={`${tipIdx}-${lang}`}>
            <span style={{ fontSize: '2rem', flexShrink: 0, lineHeight: 1 }}>{tip.icon}</span>
            <div>
              <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.95rem', marginBottom: '4px' }}>{tip.title}</div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '600', lineHeight: 1.5 }}>{tip.text}</div>
            </div>
          </div>
          <div className="tip-dots">
            {tips.map((_, i) => <div key={i} className={`tip-dot ${i === tipIdx % tips.length ? 'active' : ''}`} onClick={() => setTipIdx(i)} />)}
          </div>
        </div>

        {/* Referral */}
        <div className="referral-wrap">
          <div>
            <h3 style={{ margin: '0 0 4px', fontWeight: '900', color: 'white', fontSize: '1rem' }}>{S.referralTitle}</h3>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', fontWeight: '600' }}>{S.referralSub}</p>
          </div>
          <a href={`${WA}${referralMsg}`} target="_blank" rel="noopener noreferrer" className="referral-btn">{S.referralBtn}</a>
        </div>

        {/* Next class */}
        {nextClass && (
          <div className="next-banner">
            <div>
              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '5px' }}>{S.nextClass}</div>
              <div style={{ fontSize: '1.05rem', fontWeight: '800', color: 'white', marginBottom: '3px' }}>{nextClass.title}{nextClass.teacher_name ? ` · ${nextClass.teacher_name}` : ''}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)' }}>
                {new Date(nextClass.class_time).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <a href={nextClass.meet_link} target="_blank" rel="noopener noreferrer" className="join-btn">🎥 {lang === 'es' ? 'ENTRAR A CLASE' : 'ENTRAR NA AULA'}</a>
          </div>
        )}

        {/* Legend */}
        <div className="legend-row">
          {[['#1e3a8a', S.legend.individual], ['#d97706', S.legend.eliteInd], ['#7e22ce', S.legend.eliteSquad], ['#0d9488', S.legend.convClub], ['#0284c7', S.legend.tutoria], ['#16a34a', S.legend.enrolled], ['#dc2626', S.legend.full], ['#94a3b8', S.legend.upgrade]].map(([c, l]) => (
            <div key={l} className="leg-pill"><span className="col-dot" style={{ background: c }} />{l}</div>
          ))}
        </div>

        {/* Calendar */}
        <div id="cal">
          <FullCalendar
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            timeZone="local" locale={calLocale} firstDay={1} hiddenDays={[0, 6]}
            allDaySlot={false} slotMinTime="07:00:00" slotMaxTime="22:00:00" nowIndicator
            dayHeaderFormat={{ weekday: 'short', day: '2-digit', omitCommas: true }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: false }}
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', omitZeroMinute: false, meridiem: false }}
            events={events}
            eventClick={info => {
              const ep = info.event.extendedProps;
              if (ep.isLocked || ep.paywall) { setPaywallType(ep.paywall || 'plan_upgrade'); setPaywallOpen(true); return; }
              setStatus(''); setSelectedLesson({ id: info.event.id, title: info.event.title, extendedProps: ep });
            }}
            height="auto"
          />
        </div>

        {/* Footer */}
        <footer className="app-footer">
          <div className="app-footer-links">
            {[
              { label: S.footerSite, href: 'https://idiomasquevedo.netlify.app/' },
              { label: S.footerInsta, href: 'https://www.instagram.com/idiomasquevedo/' },
              { label: S.footerWA, href: 'https://api.whatsapp.com/send?phone=553198011835' },
            ].map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="app-footer-link">{label}</a>
            ))}
          </div>
          <p style={{ fontSize: '0.72rem', color: '#cbd5e1', margin: 0 }}>© 2026 Idiomas Quevedo · Todos los derechos reservados</p>
        </footer>

        </>}

        {/* ── MODALS ── */}

        {/* Check-in */}
        {checkinOpen && !thisWeekCheckin && (
          <div className="modal-overlay">
            <div className="modal-box" style={{ maxWidth: '440px' }}>
              <button className="modal-close" onClick={() => setCheckinOpen(false)}>✕</button>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', padding: '4px 12px', borderRadius: '50px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>{S.checkinTitle}</span>
              </div>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '16px auto 20px' }} />
              <p style={{ fontWeight: '800', fontSize: '1.05rem', color: '#0f172a', margin: '0 0 20px', lineHeight: 1.4 }}>{thisWeekCheckinQ.q}</p>
              <div style={{ textAlign: 'left' }}>
                {thisWeekCheckinQ.opts.map(opt => (
                  <button key={opt} className={`checkin-opt ${checkinSelected === opt ? 'selected' : ''}`} onClick={() => saveCheckin(opt)} disabled={checkinSaving}>
                    {checkinSaving && checkinSelected === opt ? '⏳' : opt}
                  </button>
                ))}
              </div>
              <button className="btn-ghost" onClick={() => setCheckinOpen(false)}>{S.checkinLater}</button>
            </div>
          </div>
        )}

        {/* Goal modal */}
        {goalModalOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setGoalModalOpen(false)}>
            <div className="modal-box" style={{ maxWidth: '440px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => { setGoalModalOpen(false); setEditingGoal(false); }}>✕</button>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '2.5rem' }}>🎯</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.3rem' }}>{editingGoal ? S.updateGoal : S.setGoal}</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>{S.setGoalSub}</p>
              </div>
              <div style={{ marginBottom: '8px' }}>
                {S.goalExamples.map(ex => (
                  <div key={ex} onClick={() => setGoalText(ex)} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', marginBottom: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: '#475569', border: goalText === ex ? '2px solid #dc2626' : '2px solid transparent', transition: 'all 0.15s' }}>
                    {ex}
                  </div>
                ))}
              </div>
              <textarea rows={3} placeholder={S.goalPlaceholder} value={goalText} onChange={e => setGoalText(e.target.value)} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontFamily: 'Plus Jakarta Sans', fontSize: '0.9rem', lineHeight: 1.5, resize: 'none', boxSizing: 'border-box', marginTop: '8px' }} />
              <button onClick={saveGoal} disabled={goalSaving || !goalText.trim()} style={{ width: '100%', padding: '14px', marginTop: '12px', background: goalText.trim() ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : '#f1f5f9', color: goalText.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: goalText.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans' }}>
                {goalSaving ? S.savingGoal : editingGoal ? S.updateGoal : S.saveGoal}
              </button>
              {!editingGoal && <button className="btn-ghost" onClick={() => setGoalModalOpen(false)}>{S.setLater}</button>}
            </div>
          </div>
        )}

        {/* Upcoming modal */}
        {upcomingOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setUpcomingOpen(false)}>
            <div className="modal-box modal-wide" style={{ maxWidth: '480px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => setUpcomingOpen(false)}>✕</button>
              <h2 style={{ margin: '0 0 4px', fontWeight: '900', color: '#0f172a', paddingRight: '40px' }}>{S.upcomingModal}</h2>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '20px' }}>{upcomingLessons.length} {S.upcomingClasses}</p>
              {upcomingLessons.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📭</div>
                  <div style={{ fontWeight: '700' }}>{S.noClasses}</div>
                  <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>{S.noClassesSub}</div>
                </div>
              ) : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {upcomingLessons.map(l => (
                    <div key={l.id} className="upcoming-item">
                      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
                        {l.title === 'Club de Conversación' ? '🗣️' : l.title.includes('Elite') ? '⚡' : l.title === 'Tutoría de Dudas' ? '❓' : '📚'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>{l.title}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>
                          {new Date(l.class_time).toLocaleString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {l.teacher_name ? ` · ${l.teacher_name}` : ''}
                        </div>
                      </div>
                      {l.meet_link && (
                        <a href={l.meet_link} target="_blank" rel="noopener noreferrer" style={{ background: '#16a34a', color: 'white', padding: '8px 14px', borderRadius: '10px', fontWeight: '800', fontSize: '0.75rem', textDecoration: 'none', flexShrink: 0 }}>🎥 Meet</a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setUpcomingOpen(false)} style={{ width: '100%', padding: '14px', marginTop: '16px', background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.close}</button>
            </div>
          </div>
        )}

        {/* Lesson booking modal */}
        {selectedLesson && !cancelModalOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setSelectedLesson(null)}>
            <div className="modal-box">
              <button className="modal-close" onClick={() => setSelectedLesson(null)}>✕</button>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '0 auto 24px' }} />
              <h2 style={{ fontWeight: '900', fontSize: '1.4rem', color: '#0f172a', margin: '0 0 14px' }}>
                {selectedLesson.extendedProps.isEnrolled ? S.myBooking : S.bookSlot}
              </h2>
              <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '14px', marginBottom: '16px', textAlign: 'left', border: '1.5px solid #f1f5f9' }}>
                <p style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: '800', color: '#dc2626' }}>{selectedLesson.title.replace('✅ ', '').replace('🚫 ', '')}</p>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem', fontWeight: '600' }}>
                  {new Date(selectedLesson.extendedProps.time).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {status === '' && (
                <>
                  {selectedLesson.extendedProps.isEnrolled ? (
                    <>
                      {selectedLesson.extendedProps.meet_link && (
                        <a href={selectedLesson.extendedProps.meet_link} target="_blank" rel="noopener noreferrer" className="join-btn-modal">{S.joinClass}</a>
                      )}
                      <button onClick={() => { setCancelReason(''); setCancelModalOpen(true); }} className="btn-main btn-cancel-vaga" style={{ marginTop: '10px' }}>{S.cancelSlot}</button>
                    </>
                  ) : (
                    <button onClick={handleAction} className="btn-main" style={{ background: selectedLesson.extendedProps.isFull ? 'linear-gradient(135deg,#ff6a00,#ff8c00)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
                      {selectedLesson.extendedProps.isFull ? S.joinWaitlist : S.confirmBooking}
                    </button>
                  )}
                </>
              )}
              {status === 'loading'   && <p style={{ color: '#64748b', fontWeight: '600', marginTop: '16px' }}>{S.processing}</p>}
              {status === 'success'   && <p style={{ color: '#16a34a', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>{S.booked}</p>}
              {status === 'cancelled' && <p style={{ color: '#ef4444', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>{S.cancelled}</p>}
              {status === 'waitlist'  && <p style={{ color: '#ff6a00', fontWeight: '900', fontSize: '1.1rem', marginTop: '16px' }}>{S.waitlisted}</p>}
              {status === 'already'   && <p style={{ color: '#d97706', fontWeight: '700', marginTop: '16px' }}>{S.alreadyBooked}</p>}
            </div>
          </div>
        )}

        {/* Cancel modal */}
        {cancelModalOpen && selectedLesson && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setCancelModalOpen(false)}>
            <div className="modal-box" style={{ maxWidth: '400px', textAlign: 'left' }}>
              <button className="modal-close" onClick={() => setCancelModalOpen(false)}>✕</button>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '2.5rem' }}>😕</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.2rem' }}>{S.cancelModal}</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>{S.cancelSub}</p>
              </div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>{S.cancelReason}</div>
                {CANCEL_REASONS[lang].map(r => (
                  <button key={r} onClick={() => setCancelReason(cancelReason === r ? '' : r)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '10px', border: `2px solid ${cancelReason === r ? '#dc2626' : '#f1f5f9'}`, background: cancelReason === r ? '#fee2e2' : '#f8fafc', color: cancelReason === r ? '#dc2626' : '#475569', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '6px' }}>
                    {r}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setCancelModalOpen(false)} style={{ flex: 1, padding: '13px', background: 'white', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.keepSlot}</button>
                <button onClick={confirmCancel} style={{ flex: 1, padding: '13px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.yesCancel}</button>
              </div>
            </div>
          </div>
        )}

        {/* Plan upgrade paywall */}
        {paywallOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setPaywallOpen(false)}>
            <div className="modal-box modal-wide" style={{ padding: '32px 28px' }}>
              <button className="modal-close" onClick={() => setPaywallOpen(false)}>✕</button>
              <div style={{ width: '44px', height: '5px', background: '#e2e8f0', borderRadius: '10px', margin: '0 auto 20px' }} />
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '3rem' }}>⚡</span>
                <h2 style={{ margin: '10px 0 4px', fontWeight: '900', color: '#0f172a', fontSize: '1.3rem' }}>{S.upgradeTitle}</h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>{S.upgradeSub}</p>
              </div>
              <a href={`${WA}${encodeURIComponent('Oi! Quero saber mais sobre o upgrade do meu plano de Espanhol.')}`} target="_blank" rel="noopener noreferrer" className="wa-btn">{S.waUpgrade}</a>
              <button onClick={() => setPaywallOpen(false)} style={{ width: '100%', padding: '12px', background: 'none', border: 'none', color: '#94a3b8', fontWeight: '600', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginTop: '8px', fontSize: '0.85rem' }}>{S.waNotNow}</button>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
